import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, CheckCircle2, RotateCcw, Package,
  Trash2, RefreshCw, X, MousePointer2, PlusCircle,
  Layers, Ruler, Move, ChevronDown, ChevronUp,
  Box, ArrowRightLeft, Sliders, ShieldCheck, Check, Building2, AlertTriangle,
  ArrowRight, ArrowLeft, Users, User, Maximize2,
  Clock, Battery, BatteryCharging, Zap, Calendar,
  Copy, ClipboardPaste
} from 'lucide-react';
import {
  OpenBasePalletSVG,
  ClosedBasePalletSVG,
  PalletPresetThumbnail,
  PalletLoadVisualizer
} from './PalletGraphics';

export default function PorscheConfiguratorPanel({
  mapData, setMapData,
  scaleCalibration, setScaleCalibration, confirmCalibration, cancelCalibration,
  isCalibrating, setIsCalibrating,
  selectedArea, setSelectedArea,
  selectedAreaIds = [], setSelectedAreaIds,
  updateAreaProp, deleteArea,
  selectedCorridor, setSelectedCorridor,
  selectedCorridorIds = [], setSelectedCorridorIds,
  updateCorridorProp, deleteCorridor,
  copySelected, pasteSelected, duplicateSelected, deleteSelected,
  autoConnections, deleteConnection, regeneratePaths,
  mode, setMode,
  activeStep, setActiveStep,
  activeRackType, setActiveRackType,
  activeLaneType, setActiveLaneType,
  getPalletCapacity,
  handleImageUpload, isUploading,
  selectingForFlow, setSelectingForFlow,
  createBlankWarehouse,
  activeSection = 'layout',
  setActiveSection,
  onNavigateTab
}) {
  // Accordion open/close states - starts at Menu 1 (layout) by default!
  const [openSections, setOpenSections] = useState({
    layout: activeSection ? activeSection === 'layout' : true,
    pallets: activeSection ? activeSection === 'pallets' : false,
    racking: activeSection ? activeSection === 'racking' : false,
    corridors: activeSection ? activeSection === 'corridors' : false,
    stations: activeSection ? activeSection === 'stations' : false,
    shifts: activeSection ? activeSection === 'shifts' : false
  });

  // Keep layout open whenever calibrating
  useEffect(() => {
    if (isCalibrating) {
      setOpenSections(prev => ({ ...prev, layout: true }));
    }
  }, [isCalibrating]);

  // Sync accordion ONLY when activeSection is set from parent (e.g. 2nd row top stepper)
  const prevSectionRef = useRef(activeSection);
  useEffect(() => {
    if (activeSection && prevSectionRef.current !== activeSection) {
      prevSectionRef.current = activeSection;
      setOpenSections({
        layout: activeSection === 'layout',
        pallets: activeSection === 'pallets',
        racking: activeSection === 'racking',
        corridors: activeSection === 'corridors',
        stations: activeSection === 'stations',
        shifts: activeSection === 'shifts'
      });
      const hasAreas = mapData.areas && mapData.areas.length > 0;
      const hasCorridors = mapData.corridors && mapData.corridors.length > 0;
      if (activeSection === 'layout') setActiveStep(1);
      else if (activeSection === 'pallets') setActiveStep(3);
      else if (activeSection === 'racking') { setActiveStep(4); setMode(hasAreas ? 'select' : 'draw_area'); }
      else if (activeSection === 'corridors') { setActiveStep(5); setMode(hasCorridors ? 'select' : 'draw_corridor'); }
      else if (activeSection === 'stations') { setActiveStep(6); setMode(hasAreas ? 'select' : 'draw_area'); }
      else if (activeSection === 'shifts') { setActiveStep(7); }
    }
  }, [activeSection, setActiveStep, setMode]);

  const toggleSection = (sec) => {
    setOpenSections(prev => {
      const nextState = !prev[sec];
      if (nextState && setActiveSection) {
        prevSectionRef.current = sec;
        setActiveSection(sec);
      }
      return { ...prev, [sec]: nextState };
    });
  };

  const switchToSection = (sec) => {
    prevSectionRef.current = sec;
    setOpenSections({
      layout: sec === 'layout',
      pallets: sec === 'pallets',
      racking: sec === 'racking',
      corridors: sec === 'corridors',
      stations: sec === 'stations',
      shifts: sec === 'shifts'
    });
    if (setActiveSection) setActiveSection(sec);
    const hasAreas = mapData.areas && mapData.areas.length > 0;
    const hasCorridors = mapData.corridors && mapData.corridors.length > 0;
    if (sec === 'layout') setActiveStep(1);
    else if (sec === 'pallets') setActiveStep(3);
    else if (sec === 'racking') { setActiveStep(4); setMode(hasAreas ? 'select' : 'draw_area'); }
    else if (sec === 'corridors') { setActiveStep(5); setMode(hasCorridors ? 'select' : 'draw_corridor'); }
    else if (sec === 'stations') { setActiveStep(6); setMode(hasAreas ? 'select' : 'draw_area'); }
    else if (sec === 'shifts') { setActiveStep(7); }
  };

  // Shift & Break Calculation Helpers
  const shiftConfig = mapData.shiftConfig || {
    shiftsPerDay: 2,
    shiftHours: 8,
    daysPerWeek: 6,
    lunchBreakMinutes: 60,
    shortBreakMinutes: 15,
    shortBreakCount: 2,
    opportunityCharging: true,
    dailyPalletDemand: 350,
    batteryType: 'li_ion'
  };

  const updateShiftConfig = (key, value) => {
    setMapData(prev => ({
      ...prev,
      shiftConfig: {
        ...(prev.shiftConfig || {
          shiftsPerDay: 2,
          shiftHours: 8,
          daysPerWeek: 6,
          lunchBreakMinutes: 60,
          shortBreakMinutes: 15,
          shortBreakCount: 2,
          opportunityCharging: true,
          dailyPalletDemand: 350,
          batteryType: 'li_ion'
        }),
        [key]: value
      }
    }));
  };

  const totalBreakMinutes = (shiftConfig.lunchBreakMinutes || 60) + ((shiftConfig.shortBreakMinutes || 15) * (shiftConfig.shortBreakCount || 2));
  const totalShiftMinutes = (shiftConfig.shiftHours || 8) * 60;
  const netHoursPerShift = Math.max(1, Number(((totalShiftMinutes - totalBreakMinutes) / 60).toFixed(1)));
  const netDailyHours = Number((netHoursPerShift * (shiftConfig.shiftsPerDay || 2)).toFixed(1));
  const dailyDemand = shiftConfig.dailyPalletDemand || 350;
  const requiredThroughputHr = Math.round(dailyDemand / Math.max(1, netDailyHours));
  const calculatedFleet = Math.max(1, Math.ceil(requiredThroughputHr / 14));
  const estimatedRechargePct = Math.min(85, Math.round(totalBreakMinutes * 0.5));

  // When user selects an area on the canvas, auto-expand Racking or Stations section
  useEffect(() => {
    if (selectedArea) {
      if (selectedArea.type === 'pickup' || selectedArea.type === 'dropoff') {
        setOpenSections(prev => ({ ...prev, stations: true }));
        setActiveStep(6);
        if (setActiveSection) {
          prevSectionRef.current = 'stations';
          setActiveSection('stations');
        }
      } else {
        setOpenSections(prev => ({ ...prev, racking: true }));
        setActiveStep(4);
        if (setActiveSection) {
          prevSectionRef.current = 'racking';
          setActiveSection('racking');
        }
        if (selectedArea.type) {
          setActiveRackType(selectedArea.type);
        }
      }

      // Smoothly scroll the sidebar to the Selected Area drawer
      setTimeout(() => {
        const drawerEl = document.getElementById('selected-area-drawer') || document.getElementById('selected-station-drawer');
        if (drawerEl) {
          drawerEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 80);
    }
  }, [selectedArea?.id, setActiveStep, setActiveRackType, setActiveSection]);

  // When user selects a corridor on canvas, auto-expand Corridors section
  useEffect(() => {
    if (selectedCorridor) {
      setOpenSections(prev => ({ ...prev, corridors: true }));
      setActiveStep(5);
      if (setActiveSection) {
        prevSectionRef.current = 'corridors';
        setActiveSection('corridors');
      }
    }
  }, [selectedCorridor?.id, setActiveStep, setActiveSection]);

  // Dimensions input state (Width, Length, Area in meters/sq.m.)
  const [dimState, setDimState] = useState({ w: '', l: '', area: '' });
  const [showAdvancedRack, setShowAdvancedRack] = useState(false);
  const [available3DModels, setAvailable3DModels] = useState([]);

  useEffect(() => {
    let isMounted = true;
    fetch('/api/models-3d')
      .then(res => res.json())
      .then(json => {
        if (isMounted && json.status === 'success' && Array.isArray(json.data)) {
          setAvailable3DModels(json.data);
        }
      })
      .catch(err => console.warn('Could not load 3D models for panel:', err));
    return () => { isMounted = false; };
  }, []);

  // Auto-sync modelConfig: match by modelId, fileUrl, or modelName cleanly without destroying user model
  useEffect(() => {
    if (selectedArea && selectedArea.type === 'model_3d' && available3DModels.length > 0) {
      const currentId = selectedArea.modelConfig?.modelId;
      const currentUrl = selectedArea.modelConfig?.fileUrl;
      const currentName = selectedArea.modelConfig?.modelName || selectedArea.label;

      const matched = available3DModels.find(m => 
        (currentId && String(m.id) === String(currentId)) ||
        (currentUrl && m.file_url === currentUrl) ||
        (currentName && m.name === currentName)
      );

      if (matched) {
        // If matched but modelId was missing or outdated, sync it without clobbering model
        if (String(currentId) !== String(matched.id) || !currentUrl) {
          updateAreaProp('modelConfig', {
            ...selectedArea.modelConfig,
            modelId: String(matched.id),
            modelName: matched.name,
            category: matched.category,
            fileUrl: matched.file_url,
            targetHeight: selectedArea.modelConfig?.targetHeight || matched.target_height_m || 1.70,
            rotationDeg: selectedArea.modelConfig?.rotationDeg ?? 0
          });
        }
      } else if (!currentId && !currentUrl && (!currentName || currentName === '3D Asset')) {
        // Only if completely unconfigured, assign first model
        const first = available3DModels[0];
        updateAreaProp({
          label: first.name,
          modelConfig: {
            ...selectedArea.modelConfig,
            modelId: String(first.id),
            modelName: first.name,
            category: first.category,
            fileUrl: first.file_url,
            targetHeight: first.target_height_m || 1.70,
            rotationDeg: selectedArea.modelConfig?.rotationDeg ?? 0
          }
        });
      }
    }
  }, [selectedArea?.id, selectedArea?.type, selectedArea?.modelConfig?.modelId, selectedArea?.modelConfig?.fileUrl, available3DModels]);

  useEffect(() => {
    if (selectedArea && selectedArea.w && selectedArea.h) {
      const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
      const wM = (selectedArea.w / ppm) / 1000;
      const lM = (selectedArea.h / ppm) / 1000;
      setDimState(prev => {
        const prevW = parseFloat(prev.w);
        const prevL = parseFloat(prev.l);
        const wMatches = !isNaN(prevW) && Math.abs(prevW - wM) < 0.01;
        const lMatches = !isNaN(prevL) && Math.abs(prevL - lM) < 0.01;

        return {
          w: wMatches ? prev.w : wM.toFixed(1),
          l: lMatches ? prev.l : lM.toFixed(1),
          area: (wM * lM).toFixed(1)
        };
      });
    }
  }, [selectedArea?.id, selectedArea?.w, selectedArea?.h, scaleCalibration?.pixelsPerMm]);

  const handleWidthChange = (valStr) => {
    setDimState(prev => {
      const wVal = parseFloat(valStr);
      const lVal = parseFloat(prev.l) || 0;
      return {
        ...prev,
        w: valStr,
        area: !isNaN(wVal) && !isNaN(lVal) && wVal > 0 && lVal > 0 ? (wVal * lVal).toFixed(1) : prev.area
      };
    });
    const wVal = parseFloat(valStr);
    if (!isNaN(wVal) && wVal > 0) {
      const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
      const newW_px = wVal * 1000 * ppm;
      updateAreaProp('w', newW_px);
    }
  };

  const handleLengthChange = (valStr) => {
    setDimState(prev => {
      const lVal = parseFloat(valStr);
      const wVal = parseFloat(prev.w) || 0;
      return {
        ...prev,
        l: valStr,
        area: !isNaN(wVal) && !isNaN(lVal) && wVal > 0 && lVal > 0 ? (wVal * lVal).toFixed(1) : prev.area
      };
    });
    const lVal = parseFloat(valStr);
    if (!isNaN(lVal) && lVal > 0) {
      const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
      const newH_px = lVal * 1000 * ppm;
      updateAreaProp('h', newH_px);
    }
  };

  const handleAreaChange = (valStr) => {
    const aVal = parseFloat(valStr);
    const wVal = parseFloat(dimState.w) || 0;
    if (!isNaN(aVal) && aVal > 0 && wVal > 0) {
      const newL = aVal / wVal;
      setDimState(prev => ({
        ...prev,
        area: valStr,
        l: newL.toFixed(1)
      }));
      const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
      const newH_px = newL * 1000 * ppm;
      updateAreaProp('h', newH_px);
    } else {
      setDimState(prev => ({ ...prev, area: valStr }));
    }
  };

  // ─── Pallet & Load Definitions (Presets & Multi-Type Management) ───
  const PALLET_PRESETS = [
    {
      id: 'loscam',
      name: 'Loscam Pallet (ไม้แดง)',
      brand: 'loscam',
      entryType: 'open',
      width: 1000,
      length: 1200,
      baseHeight: 150,
      height: 1500,
      overhang: 0,
      maxWeight: 1000,
      color: '#e11d48',
      badge: 'Loscam (ไม้แดง)',
      desc: 'พาเลทไม้มาตรฐาน Loscam 4-Way Open นิยมใช้มากที่สุดในศูนย์กระจายสินค้าไทย'
    },
    {
      id: 'chep',
      name: 'CHEP Pallet (ไม้น้ำเงิน)',
      brand: 'chep',
      entryType: 'closed',
      width: 1000,
      length: 1200,
      baseHeight: 150,
      height: 1500,
      overhang: 0,
      maxWeight: 1200,
      color: '#2563eb',
      badge: 'CHEP (ไม้น้ำเงิน)',
      desc: 'พาเลทไม้ CHEP ฐาน Perimeter ปิดล้อมรอบ เหมาะกับ Counterbalance & Reach Truck'
    },
    {
      id: 'wooden',
      name: 'พาเลทไม้เปิดทั่วไป (Wooden 4-Way)',
      brand: 'wooden',
      entryType: 'open',
      width: 1000,
      length: 1200,
      baseHeight: 150,
      height: 1400,
      overhang: 0,
      maxWeight: 1000,
      color: '#d97706',
      badge: 'ไม้ทั่วไป (เปิด)',
      desc: 'พาเลทไม้ฐานเปิด รองรับ Hand Pallet และ Stacker ได้ทุกรุ่น'
    },
    {
      id: 'plastic',
      name: 'พาเลทพลาสติก (Plastic Cleanroom)',
      brand: 'plastic',
      entryType: 'closed',
      width: 1100,
      length: 1100,
      baseHeight: 150,
      height: 1300,
      overhang: 0,
      maxWeight: 1000,
      color: '#059669',
      badge: 'พลาสติก (1100×1100)',
      desc: 'พาเลทพลาสติกทนสารเคมี ล้างทำความสะอาดได้ เหมาะกับคลังอาหาร ยา และห้องเย็น'
    },
    {
      id: 'euro',
      name: 'Euro Pallet (EPAL 800×1200)',
      brand: 'custom',
      entryType: 'open',
      width: 800,
      length: 1200,
      baseHeight: 144,
      height: 1400,
      overhang: 0,
      maxWeight: 1500,
      color: '#8b5cf6',
      badge: 'Euro EPAL',
      desc: 'พาเลทมาตรฐานยุโรป กว้าง 800 mm ขนส่งตู้คอนเทนเนอร์และนำเข้าต่างประเทศ'
    }
  ];

  // Pallet types list from mapData
  const palletList = (mapData.palletTypes && mapData.palletTypes.length > 0)
    ? mapData.palletTypes
    : [
        {
          id: 'pt_1',
          name: 'Loscam Pallet (พาเลทหลัก)',
          brand: mapData.palletSize?.brand || 'loscam',
          entryType: mapData.palletSize?.entryType || 'open',
          width: mapData.palletSize?.width || 1000,
          length: mapData.palletSize?.length || 1200,
          baseHeight: mapData.palletSize?.height || 150,
          height: mapData.palletSize?.totalHeight || 1500,
          overhang: mapData.palletSize?.overhang || 0,
          maxWeight: mapData.palletSize?.maxWeight || 1000,
          color: '#e11d48',
          isPrimary: true
        }
      ];

  const [activePalletId, setActivePalletId] = useState(palletList[0]?.id || 'pt_1');

  // Keep activePalletId valid
  const currentPallet = palletList.find(p => p.id === activePalletId) || palletList[0];

  const updatePalletType = (id, updates) => {
    const updated = palletList.map(p => {
      if (p.id === id) {
        return { ...p, ...updates };
      }
      return p;
    });

    const primary = updated.find(p => p.isPrimary) || updated[0];
    setMapData(prev => ({
      ...prev,
      palletTypes: updated,
      palletSize: {
        width: primary.width,
        length: primary.length,
        height: primary.baseHeight || 150,
        totalHeight: primary.height || 1500,
        entryType: primary.entryType || 'open',
        brand: primary.brand || 'loscam',
        overhang: primary.overhang || 0,
        maxWeight: primary.maxWeight || 1000
      }
    }));
  };

  const handleAddPalletType = () => {
    const newId = `pt_${Date.now()}`;
    const newPallet = {
      id: newId,
      name: `รูปแบบพาเลท #${palletList.length + 1}`,
      brand: 'chep',
      entryType: 'closed',
      width: 1000,
      length: 1200,
      baseHeight: 150,
      height: 1500,
      overhang: 0,
      maxWeight: 1200,
      color: '#2563eb',
      isPrimary: false
    };
    const updated = [...palletList, newPallet];
    setMapData(prev => ({ ...prev, palletTypes: updated }));
    setActivePalletId(newId);
  };

  const handleDeletePalletType = (id) => {
    if (palletList.length <= 1) return;
    const updated = palletList.filter(p => p.id !== id);
    if (!updated.some(p => p.isPrimary)) {
      updated[0].isPrimary = true;
    }
    const primary = updated.find(p => p.isPrimary) || updated[0];
    setMapData(prev => ({
      ...prev,
      palletTypes: updated,
      palletSize: {
        width: primary.width,
        length: primary.length,
        height: primary.baseHeight || 150,
        totalHeight: primary.height || 1500,
        entryType: primary.entryType || 'open',
        brand: primary.brand || 'loscam',
        overhang: primary.overhang || 0,
        maxWeight: primary.maxWeight || 1000
      }
    }));
    setActivePalletId(updated[0].id);
  };

  const handleSetPrimaryPallet = (id) => {
    const updated = palletList.map(p => ({
      ...p,
      isPrimary: p.id === id
    }));
    const primary = updated.find(p => p.id === id);
    setMapData(prev => ({
      ...prev,
      palletTypes: updated,
      palletSize: {
        width: primary.width,
        length: primary.length,
        height: primary.baseHeight || 150,
        totalHeight: primary.height || 1500,
        entryType: primary.entryType || 'open',
        brand: primary.brand || 'loscam',
        overhang: primary.overhang || 0,
        maxWeight: primary.maxWeight || 1000
      }
    }));
  };

  const handleApplyPreset = (preset) => {
    updatePalletType(currentPallet.id, {
      name: preset.name,
      brand: preset.brand,
      entryType: preset.entryType,
      width: preset.width,
      length: preset.length,
      baseHeight: preset.baseHeight,
      height: preset.height,
      overhang: preset.overhang,
      maxWeight: preset.maxWeight,
      color: preset.color
    });
  };

  // Calculations
  const rackAreas = (mapData.areas || []).filter(a => a.type?.startsWith('storage') || a.type === 'conveyor');
  const totalPallets = rackAreas.reduce((sum, a) => {
    if (a.palletCount) return sum + a.palletCount;
    const c = getPalletCapacity(a);
    return sum + (c?.total || 0);
  }, 0);

  // Racking options definition (Porsche Configurator Style)
  const RACK_OPTIONS = [
    {
      type: 'storage_selective_rack',
      name: 'Selective Rack',
      thai: 'แร็คมาตรฐาน (100% Selectivity)',
      desc: 'เข้าถึงทุกพาเลทได้อิสระ ทางเดินรถกว้าง ~2.8 - 3.2m',
      badge: 'Standard',
      color: '#0ea5e9',
      image: '/images/racks/selective_rack.jpg',
      defaultAisle: 3000,
    },
    {
      type: 'storage_2way_shuttle',
      name: '2-Way Shuttle Rack',
      thai: 'แร็คชัทเทิลความหนาแน่นสูง',
      desc: 'เพิ่มความจุจัดเก็บ +60% พาเลทเลื่อนบนรางอัตโนมัติ',
      badge: 'High Density',
      color: '#f59e0b',
      image: '/images/racks/shuttle_2way.jpg',
      defaultAisle: 1600,
    },
    {
      type: 'storage_4way_shuttle',
      name: '4-Way Shuttle AS/RS',
      thai: 'ระบบออโตเมชั่น 3D Grid',
      desc: 'ลำเลียง 4 ทิศทาง ความจุสูงสุดสำหรับคลังอัตโนมัติ',
      badge: 'Advanced AS/RS',
      color: '#ec4899',
      image: '/images/racks/shuttle_4way.jpg',
      defaultAisle: 1500,
    },
    {
      type: 'conveyor',
      name: 'Conveyor System',
      thai: 'ระบบสายพานลำเลียงอัตโนมัติ',
      desc: 'สายพานลูกกลิ้งลำเลียงพาเลทและกล่องสินค้า เชื่อมต่อระหว่างโซน',
      badge: 'Automation',
      color: '#4b5563',
      image: '/images/racks/conveyor.jpg',
      defaultAisle: 1200,
    },
    {
      type: 'storage',
      name: 'Floor Staging / Bulk',
      thai: 'พื้นที่วางพาเลทบนพื้น',
      desc: 'กองพาเลทบนพื้นหรือซ้อน 2-3 ชั้น สำหรับ Buffer และ staging',
      badge: 'Floor Area',
      color: '#6366f1',
      image: '/images/racks/floor_staging.jpg',
      defaultAisle: 2500,
    },
    {
      type: 'model_3d',
      name: '3D Model / Asset Area',
      thai: 'พื้นที่วางโมเดล 3D (Area Design)',
      desc: 'วางโมเดล 3D เช่น รถ Reach Truck, Forklift, พนักงาน หรืออุปกรณ์ประกอบผัง',
      badge: '3D Asset',
      color: '#8b5cf6',
      image: '/images/racks/model_3d.jpg',
      defaultAisle: 2000,
    }
  ];

  // Corridor options
  const CORRIDOR_OPTIONS = [
    {
      id: '2way',
      name: 'Dual Lane (2-Way)',
      thai: 'เลนสวน 2 ทิศทาง',
      desc: 'กว้าง >= 2,800mm รองรับรถวิ่งสวนกัน',
      lanes: 2,
      dir: 'bidirectional',
      color: '#f59e0b',
    },
    {
      id: '1way',
      name: 'Single Lane (1-Way)',
      thai: 'เลนเดี่ยว ทางเดียว',
      desc: 'กว้าง 1,400 - 2,000mm เหมาะกับ Aisle แคบ',
      lanes: 1,
      dir: 'forward',
      color: '#3b82f6',
    },
    {
      id: 'split',
      name: 'Split Lane (ซ้าย/ขวา)',
      thai: 'เลนแยกทิศทางไป-กลับ',
      desc: 'ระบบทางคู่แยกเลนชัดเจน ปลอดภัยสูง',
      lanes: 2,
      dir: 'split',
      color: '#10b981',
    }
  ];

  // Station options
  const STATION_OPTIONS = [
    {
      type: 'pickup',
      name: 'Inbound Pickup',
      thai: 'จุดรับสินค้าเข้า (Inbound)',
      desc: 'จุดรับพาเลทเข้าจากท่าเทียบหรือฝ่ายผลิต',
      color: '#10b981',
    },
    {
      type: 'dropoff',
      name: 'Outbound Drop-off',
      thai: 'จุดจ่ายสินค้าออก (Outbound)',
      desc: 'จุดส่งพาเลทเตรียมโหลดขึ้นรถขนส่ง',
      color: '#ef4444',
    },
    {
      type: 'conveyor',
      name: 'Conveyor Line',
      thai: 'จุดเชื่อมสายพานลำเลียง',
      desc: 'เชื่อมต่อไปยังระบบสายพานอัตโนมัติ',
      color: '#4b5563',
    },
    {
      type: 'operator_station',
      name: 'Operator Workstation',
      thai: 'พื้นที่คนทำงาน / จุดตรวจเช็ค',
      desc: 'พื้นที่ยืนทำงาน คัดแยก QC หรือแพ็คสินค้า',
      color: '#059669',
    }
  ];

  const handleSelectRackType = (opt) => {
    setActiveRackType(opt.type);
    setActiveStep(4);
    if (selectedArea) {
      updateAreaProp('type', opt.type);
      if (opt.defaultAisle && selectedArea.rackConfig) {
        updateAreaProp('rackConfig', { ...selectedArea.rackConfig, aisleWidth: opt.defaultAisle });
      }
    } else {
      setMode('draw_area');
    }
  };

  const handleSelectCorridorType = (opt) => {
    setActiveLaneType(opt.id);
    setActiveStep(5);
    if (selectedCorridor) {
      updateCorridorProp('lanes', opt.lanes);
      updateCorridorProp('direction', opt.dir);
    } else {
      setMode('draw_corridor');
    }
  };

  const handleSelectStationType = (opt) => {
    setActiveRackType(opt.type);
    setActiveStep(4);
    if (selectedArea) {
      updateAreaProp('type', opt.type);
    } else {
      setMode('draw_area');
    }
  };

  return (
    <div className="w-80 lg:w-[370px] border-l border-gray-200 bg-white flex flex-col h-full overflow-hidden shadow-xs shrink-0 select-none">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white shrink-0 flex items-center justify-between">
        <div>
          <h3 className="font-extrabold text-gray-900 text-sm tracking-tight flex items-center gap-2">
            <Sliders size={16} className="text-red-600" />
            Solution Configurator
          </h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            คลิกเลือกและปรับแต่งองค์ประกอบผังคลังสินค้า
          </p>
        </div>
        <div className="text-[11px] font-bold text-gray-400 bg-gray-50 border px-2 py-1 rounded-md">
          {totalPallets.toLocaleString()} Pallets
        </div>
      </div>

      {/* Accordion Categories Body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-100/70 text-xs">

        {/* Multi-Selection Action Banner */}
        {((selectedAreaIds && selectedAreaIds.length > 1) || (selectedCorridorIds && selectedCorridorIds.length > 1)) && (
          <div className="rounded-2xl border border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50/70 p-3.5 shadow-sm space-y-2.5 animate-in fade-in duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center font-black text-xs shadow-xs">
                  {(selectedAreaIds?.length || 0) + (selectedCorridorIds?.length || 0)}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-xs">เลือกพื้นที่หลายรายการ</h4>
                  <p className="text-[10px] text-gray-500">ลากคลุมดำหรือ Shift+คลิก • ลากย้ายพร้อมกันได้</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (setSelectedAreaIds) setSelectedAreaIds([]);
                  if (setSelectedCorridorIds) setSelectedCorridorIds([]);
                  if (setSelectedArea) setSelectedArea(null);
                  if (setSelectedCorridor) setSelectedCorridor(null);
                }}
                className="text-[11px] text-gray-400 hover:text-gray-700 px-2 py-0.5 rounded hover:bg-white/80 transition-colors cursor-pointer"
                title="ยกเลิกการเลือก"
              >
                ✕ ปลดเลือก
              </button>
            </div>

            <div className="grid grid-cols-4 gap-1.5 pt-0.5">
              <button
                type="button"
                onClick={copySelected}
                className="flex items-center justify-center gap-1 px-2 py-2 bg-white hover:bg-blue-50 text-blue-700 border border-blue-200 rounded-xl font-semibold text-[11px] shadow-2xs hover:border-blue-300 transition-all cursor-pointer"
                title="คัดลอกรายการที่เลือก (Ctrl+C)"
              >
                <Copy size={13} />
                <span>คัดลอก</span>
              </button>
              <button
                type="button"
                onClick={pasteSelected}
                className="flex items-center justify-center gap-1 px-2 py-2 bg-blue-600 hover:bg-blue-700 text-white border border-blue-600 rounded-xl font-semibold text-[11px] shadow-xs transition-all cursor-pointer"
                title="วางรายการที่คัดลอก (Ctrl+V)"
              >
                <ClipboardPaste size={13} />
                <span>วาง</span>
              </button>
              {duplicateSelected && (
                <button
                  type="button"
                  onClick={duplicateSelected}
                  className="flex items-center justify-center gap-1 px-2 py-2 bg-white hover:bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl font-semibold text-[11px] shadow-2xs hover:border-indigo-300 transition-all cursor-pointer"
                  title="ทำซ้ำรายการที่เลือก (Ctrl+D)"
                >
                  <Copy size={13} />
                  <span>ทำซ้ำ</span>
                </button>
              )}
              <button
                type="button"
                onClick={deleteSelected}
                className="flex items-center justify-center gap-1 px-2 py-2 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-xl font-semibold text-[11px] shadow-2xs hover:border-red-300 transition-all cursor-pointer"
                title="ลบรายการที่เลือกทั้งหมด (Delete)"
              >
                <Trash2 size={13} />
                <span>ลบ</span>
              </button>
            </div>
          </div>
        )}

        {/* ─── 1. Floor Plan & Scale ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.layout ? 'border-blue-300 shadow-sm ring-1 ring-blue-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('layout'); setActiveStep(1); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.layout ? 'bg-gradient-to-r from-blue-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                <Layers size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">1. ผังอาคาร & ขนาดสเกล</span>
                  {(mapData.bgImageData || mapData.svgContent) ? (
                    <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                      โหลดไฟล์แล้ว
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">
                      พื้นที่โล่ง (Open Floor)
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {scaleCalibration?.pixelsPerMm 
                    ? `สเกล 1 px = ${(1 / scaleCalibration.pixelsPerMm).toFixed(1)} mm`
                    : (mapData.bgImageData || mapData.svgContent)
                      ? 'รอการเทียบระยะสเกลผัง'
                      : 'พื้นที่โล่งพร้อมออกแบบ'}
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.layout ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.layout ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.layout && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3 animate-in fade-in duration-150">
              {/* Upload Dropzone */}
              <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">ไฟล์ผังคลังสินค้า (Layout File)</span>
                  {mapData.bgImageData || mapData.svgContent ? (
                    <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                      <CheckCircle2 size={12} /> พร้อมใช้งาน
                    </span>
                  ) : null}
                </div>

                <label className={`w-full flex items-center justify-center gap-2 py-2.5 px-3 border border-gray-300 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                  isUploading ? 'bg-gray-100 text-gray-400' : 'bg-white hover:bg-gray-50 text-gray-700 hover:border-red-400 shadow-xs'
                }`}>
                  <Upload size={14} className="text-gray-500" />
                  {isUploading ? 'กำลังอัปโหลด...' : (mapData.bgImageData || mapData.svgContent) ? 'เปลี่ยนไฟล์ผังใหม่' : 'อัปโหลด JPG / PNG / PDF / DWG'}
                  <input type="file" accept="image/*,application/pdf,.dwg" className="hidden" onChange={handleImageUpload} disabled={isUploading} />
                </label>

                {/* Appearance Adjustments */}
                {(mapData.svgContent || mapData.bgImageData) && (
                  <div className="pt-2 border-t space-y-2 text-[11px]">
                    <div className="flex justify-between text-gray-500">
                      <span>ความโปร่งแสง (Opacity)</span>
                      <span>{Math.round((mapData.bgSettings?.opacity ?? 1) * 100)}%</span>
                    </div>
                    <input 
                      type="range" min="0" max="1" step="0.05" 
                      value={mapData.bgSettings?.opacity ?? 1} 
                      onChange={e => setMapData(p => ({ ...p, bgSettings: { ...p.bgSettings, opacity: parseFloat(e.target.value) } }))}
                      className="w-full accent-red-600" 
                    />
                  </div>
                )}
              </div>

              {/* Scale Calibration Card */}
              <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800 flex items-center gap-1.5">
                    <Ruler size={14} className="text-red-600" />
                    ตั้งค่าสเกลไม้บรรทัด (Scale)
                  </span>
                  {isCalibrating && (
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 animate-pulse">
                      กำลังเทียบสเกล
                    </span>
                  )}
                </div>

                {!isCalibrating && scaleCalibration?.pixelsPerMm ? (
                  <div className="space-y-2 pt-1">
                    <div className="bg-emerald-50 text-emerald-800 p-2.5 rounded-lg text-xs border border-emerald-200 flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-emerald-900">สเกลที่ตั้งไว้:</div>
                        <span>1 px = <strong>{(1 / scaleCalibration.pixelsPerMm).toFixed(2)} mm</strong></span>
                        <span className="text-gray-500 text-[10px] ml-1.5">
                          ({(1000 * scaleCalibration.pixelsPerMm).toFixed(1)} px/ม.)
                        </span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          setScaleCalibration(p => ({ ...p, point1: null, point2: null, distanceMm: '', pixelsPerMm: null }));
                          if (setIsCalibrating) setIsCalibrating(true);
                          setActiveStep(2);
                        }} 
                        className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-[11px] shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
                      >
                        <RotateCcw size={11} /> ปรับใหม่
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2.5 pt-1">
                    <div className="p-2.5 bg-blue-50/80 border border-blue-200 rounded-lg text-[11px] text-blue-900 space-y-1">
                      <div className="font-bold flex items-center gap-1 text-blue-800">
                        <Ruler size={12} /> วิธีเทียบระยะจริงบนผัง:
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        1. คลิก 2 จุด (จุด A และ จุด B) บนผังที่ทราบระยะจริง<br/>
                        2. กรอกระยะทางจริงหน่วยมิลลิเมตร (เช่น 10000 = 10 เมตร)<br/>
                        3. กดปุ่ม "ยืนยันค่าสเกล"
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className={`flex-1 p-2.5 rounded-lg border text-center text-xs transition-all ${
                        scaleCalibration?.point1 
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-bold shadow-xs' 
                          : 'bg-amber-50 border-amber-300 text-amber-800 font-medium animate-pulse ring-2 ring-amber-200'
                      }`}>
                        {scaleCalibration?.point1 
                          ? `✓ จุด A (${scaleCalibration.point1.x.toFixed(0)}, ${scaleCalibration.point1.y.toFixed(0)})` 
                          : '👉 1. คลิกจุด A บนผัง'}
                      </div>
                      <div className={`flex-1 p-2.5 rounded-lg border text-center text-xs transition-all ${
                        scaleCalibration?.point2 
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-bold shadow-xs' 
                          : scaleCalibration?.point1 
                            ? 'bg-amber-50 border-amber-300 text-amber-800 font-medium animate-pulse ring-2 ring-amber-200' 
                            : 'bg-gray-50 border-gray-200 text-gray-400'
                      }`}>
                        {scaleCalibration?.point2 
                          ? `✓ จุด B (${scaleCalibration.point2.x.toFixed(0)}, ${scaleCalibration.point2.y.toFixed(0)})` 
                          : '2. คลิกจุด B บนผัง'}
                      </div>
                    </div>

                    {scaleCalibration?.point1 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setScaleCalibration(p => ({ ...p, point1: null, point2: null }))}
                          className="text-[11px] text-gray-500 hover:text-red-600 underline flex items-center gap-1 cursor-pointer"
                        >
                          <RotateCcw size={10} /> รีเซ็ตจุดคลิก
                        </button>
                      </div>
                    )}

                    {scaleCalibration?.point1 && scaleCalibration?.point2 && (
                      <div className="space-y-2 pt-1 border-t border-gray-100">
                        <div className="flex justify-between items-center text-[11px]">
                          <span className="font-semibold text-gray-700">ระยะพิกเซลบนภาพ:</span>
                          <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                            {Math.hypot(scaleCalibration.point2.x - scaleCalibration.point1.x, scaleCalibration.point2.y - scaleCalibration.point1.y).toFixed(1)} px
                          </span>
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                            ระยะห่างจริง (mm) <span className="text-red-500">*</span>
                          </label>
                          <input 
                            type="number" 
                            min="10" 
                            step="100"
                            placeholder="เช่น 10000 (สำหรับ 10 เมตร)" 
                            value={scaleCalibration?.distanceMm || ''} 
                            onChange={(e) => setScaleCalibration(p => ({ ...p, distanceMm: e.target.value }))}
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-red-500 font-semibold text-gray-800"
                            autoFocus
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button 
                            type="button"
                            onClick={confirmCalibration}
                            disabled={!scaleCalibration?.distanceMm || parseFloat(scaleCalibration.distanceMm) <= 0}
                            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-bold rounded-lg transition-colors text-xs shadow-xs cursor-pointer"
                          >
                            ยืนยันค่าสเกล (Confirm)
                          </button>
                          {cancelCalibration && (
                            <button
                              type="button"
                              onClick={cancelCalibration}
                              className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-xs transition-colors cursor-pointer"
                            >
                              ยกเลิก
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {(!scaleCalibration?.point1 || !scaleCalibration?.point2) && cancelCalibration && (
                      <div className="pt-1 flex justify-center">
                        <button
                          type="button"
                          onClick={cancelCalibration}
                          className="text-[11px] text-gray-400 hover:text-gray-600 hover:underline cursor-pointer"
                        >
                          ข้ามไปก่อน (ใช้สเกลมาตรฐาน 1 px = 80 mm)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Advance to Step 2: Pallets */}
              <div className="pt-2 border-t border-slate-200/80">
                <button
                  type="button"
                  onClick={() => switchToSection('pallets')}
                  className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <span>ต่อไป: กำหนดสเปกพาเลท & สินค้า (ขั้นตอนที่ 2)</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── 2. Pallet & Load Specification (แทรกเป็นรายการที่สองตามความต้องการ) ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.pallets ? 'border-emerald-300 shadow-sm ring-1 ring-emerald-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('pallets'); setActiveStep(3); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.pallets ? 'bg-gradient-to-r from-emerald-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center shrink-0">
                <Package size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">2. สเปกพาเลท & ข้อมูลสินค้า</span>
                  <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                    {palletList.length} รูปแบบ
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {currentPallet.name} • {currentPallet.width}×{currentPallet.length} mm (สูง {currentPallet.height || 1500} mm, {currentPallet.maxWeight || 1000} kg)
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.pallets ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.pallets ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.pallets && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3.5 animate-in fade-in duration-150">
              {/* แถบจัดการรูปแบบพาเลท (Multi-type manager) */}
              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-xs space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800 text-xs">
                    รายการรูปแบบพาเลทในคลัง ({palletList.length} แบบ)
                  </span>
                  <button 
                    type="button"
                    onClick={handleAddPalletType}
                    className="text-[11px] text-emerald-600 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <PlusCircle size={13} /> เพิ่มรูปแบบพาเลท
                  </button>
                </div>

                {/* Pallet Type Chips */}
                <div className="flex flex-wrap gap-1.5">
                  {palletList.map((pt, idx) => {
                    const isSelected = pt.id === activePalletId;
                    return (
                      <button
                        key={pt.id}
                        type="button"
                        onClick={() => setActivePalletId(pt.id)}
                        className={`py-1 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: pt.color || '#10b981' }} />
                        <span>{pt.name || `Type ${idx + 1}`}</span>
                        {pt.isPrimary && (
                          <span className="text-[9px] bg-emerald-700/80 text-white px-1 rounded">หลัก</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Action for active pallet type */}
                <div className="flex items-center justify-between pt-1 border-t border-gray-100 text-[11px]">
                  <div>
                    {!currentPallet.isPrimary ? (
                      <button 
                        type="button"
                        onClick={() => handleSetPrimaryPallet(currentPallet.id)}
                        className="text-emerald-700 font-bold hover:underline cursor-pointer"
                      >
                        ★ ตั้งเป็นพาเลทหลักของคลัง
                      </button>
                    ) : (
                      <span className="text-emerald-700 font-bold flex items-center gap-1">
                        <Check size={12} /> เป็นพาเลทหลักสำหรับคำนวณแร็ค
                      </span>
                    )}
                  </div>
                  {palletList.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => handleDeletePalletType(currentPallet.id)}
                      className="text-red-500 hover:text-red-700 flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 size={12} /> ลบแบบนี้
                    </button>
                  )}
                </div>
              </div>

              {/* 3D Isometric Cargo & Pallet Visualizer */}
              <PalletLoadVisualizer pallet={currentPallet} />

              {/* ปรับแต่งรายละเอียดพาเลทที่เลือก */}
              <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="font-bold text-gray-900 text-xs">
                    สเปกพาเลท: {currentPallet.name}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    ID: {currentPallet.id}
                  </span>
                </div>

                {/* Presets Button Bar with 3D Isometric Thumbnails */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                    <span>เลือกสเปกมาตรฐานยอดนิยม (Quick Presets):</span>
                    <span className="text-[10px] text-gray-400 font-normal">คลิกเพื่อเลือกทันที</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {PALLET_PRESETS.map(preset => {
                      const isMatch = currentPallet.brand === preset.brand && currentPallet.entryType === preset.entryType && currentPallet.width === preset.width && currentPallet.length === preset.length;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handleApplyPreset(preset)}
                          className={`p-2 rounded-xl border text-left text-xs transition-all cursor-pointer flex flex-col justify-between group ${
                            isMatch
                              ? 'border-2 border-emerald-600 bg-emerald-50/50 shadow-xs'
                              : 'border-gray-200 hover:border-emerald-300 hover:bg-slate-50 bg-white'
                          }`}
                        >
                          {/* Real Pallet Photo Thumbnail */}
                          <div className="w-full h-16 mb-1.5 flex items-center justify-center bg-white rounded-lg p-1 group-hover:bg-slate-50 transition-colors border border-slate-200/70 shadow-2xs">
                            <PalletPresetThumbnail presetId={preset.id} isSelected={isMatch} className="w-full h-full" />
                          </div>

                          <div>
                            <div className="flex items-center justify-between">
                              <span className={`text-[11px] font-bold truncate ${isMatch ? 'text-emerald-900' : 'text-gray-900'}`}>
                                {preset.badge}
                              </span>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: preset.color }} />
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5 flex items-center justify-between">
                              <span>{preset.width}×{preset.length} mm</span>
                              <span className={`text-[9px] px-1 rounded ${preset.entryType === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                                {preset.entryType === 'open' ? 'เปิด' : 'ปิด'}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ชื่อรูปแบบพาเลท */}
                <div>
                  <label className="block text-gray-600 mb-1 text-[11px] font-semibold">ชื่อรูปแบบพาเลท (Pallet Label)</label>
                  <input 
                    type="text"
                    value={currentPallet.name || ''}
                    onChange={e => updatePalletType(currentPallet.id, { name: e.target.value })}
                    className="w-full p-2 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
                    placeholder="เช่น Loscam Standard หรือ Finished Goods"
                  />
                </div>

                {/* 1. ประเภททางเข้า: พาเลทเปิด vs พาเลทปิด พร้อมรูปภาพจริง */}
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-gray-700">
                    ประเภทโครงสร้างพาเลท (Pallet Entry Type)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Open Base Card */}
                    <div 
                      onClick={() => updatePalletType(currentPallet.id, { entryType: 'open' })}
                      className={`p-2.5 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                        currentPallet.entryType === 'open'
                          ? 'border-emerald-600 bg-emerald-50/40 text-emerald-900 shadow-xs'
                          : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {/* Photo Graphic */}
                      <OpenBasePalletSVG isSelected={currentPallet.entryType === 'open'} className="mb-2" />

                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">พาเลทเปิด (Open Base)</span>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${currentPallet.entryType === 'open' ? 'border-emerald-600' : 'border-gray-300'}`}>
                          {currentPallet.entryType === 'open' && <div className="w-2 h-2 rounded-full bg-emerald-600" />}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-relaxed">
                        ด้านล่างเปิดโล่ง • ล้อ Hand Pallet สัมผัสพื้นได้โดยตรง • ใช้ได้กับ Forklift, Reach Truck, และ Stacker ทุกรุ่น
                      </p>
                    </div>

                    {/* Closed Base Card */}
                    <div 
                      onClick={() => updatePalletType(currentPallet.id, { entryType: 'closed' })}
                      className={`p-2.5 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                        currentPallet.entryType === 'closed'
                          ? 'border-blue-600 bg-blue-50/40 text-blue-900 shadow-xs'
                          : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {/* Photo Graphic */}
                      <ClosedBasePalletSVG isSelected={currentPallet.entryType === 'closed'} className="mb-2" />

                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">พาเลทปิด (Closed Base)</span>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${currentPallet.entryType === 'closed' ? 'border-blue-600' : 'border-gray-300'}`}>
                          {currentPallet.entryType === 'closed' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-relaxed">
                        ฐานมีไม้ปิดรอบ (Perimeter) • เช่น CHEP ต้องใช้ Forklift หรือ Reach Truck ขาคร่อม
                      </p>
                    </div>
                  </div>

                  {currentPallet.entryType === 'closed' && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded-lg text-[10px] flex items-start gap-1.5 mt-1">
                      <AlertTriangle size={13} className="text-amber-600 shrink-0 mt-0.5" />
                      <span>ข้อควรระวัง: พาเลทปิดไม่สามารถใช้ Hand Stacker ขาปกติยกได้ ต้องเลือกรถยกที่มีขากว้างคร่อมพาเลทหรือ Forklift Counterbalance</span>
                    </div>
                  )}
                </div>

                {/* 2. ยี่ห้อ / มาตรฐานพาเลท */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">ยี่ห้อ/มาตรฐาน (Brand)</label>
                    <select
                      value={currentPallet.brand || 'loscam'}
                      onChange={e => updatePalletType(currentPallet.id, { brand: e.target.value })}
                      className="w-full p-2 border rounded-lg text-xs bg-white outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
                    >
                      <option value="loscam">Loscam (ไม้แดง)</option>
                      <option value="chep">CHEP (ไม้น้ำเงิน)</option>
                      <option value="wooden">พาเลทไม้ทั่วไป (Wooden)</option>
                      <option value="plastic">พาเลทพลาสติก (Plastic)</option>
                      <option value="steel">พาเลทเหล็ก (Steel)</option>
                      <option value="custom">กำหนดเอง (Custom)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">สูงตัวพาเลทเปล่า (mm)</label>
                    <input 
                      type="number" min="100" max="250" step="10"
                      value={currentPallet.baseHeight || 150}
                      onChange={e => updatePalletType(currentPallet.id, { baseHeight: parseInt(e.target.value) || 150 })}
                      className="w-full p-2 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-bold"
                    />
                  </div>
                </div>

                {/* 3. มิติขนาด กว้าง W x ยาว L */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">ความกว้าง W (mm)</label>
                    <input 
                      type="number" min="500" max="2000" step="50"
                      value={currentPallet.width || 1000}
                      onChange={e => updatePalletType(currentPallet.id, { width: parseInt(e.target.value) || 1000 })}
                      className="w-full p-2 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-bold text-gray-900"
                    />
                    <span className="text-[9px] text-gray-400">ด้านหน้ายก (Face)</span>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">ความยาว L (mm)</label>
                    <input 
                      type="number" min="500" max="2000" step="50"
                      value={currentPallet.length || 1200}
                      onChange={e => updatePalletType(currentPallet.id, { length: parseInt(e.target.value) || 1200 })}
                      className="w-full p-2 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 font-bold text-gray-900"
                    />
                    <span className="text-[9px] text-gray-400">ด้านลึกงาเสียบ (Depth)</span>
                  </div>
                </div>

                {/* 4. ความสูงสินค้ารวมพาเลท (Total Height with Load) */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-800">
                      ความสูงสินค้ารวมพาเลท (Total Height mm)
                    </label>
                    <span className="text-xs font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      {((currentPallet.height || 1500) / 1000).toFixed(2)} ม.
                    </span>
                  </div>
                  <input 
                    type="number" min="500" max="3500" step="50"
                    value={currentPallet.height || 1500}
                    onChange={e => updatePalletType(currentPallet.id, { height: parseInt(e.target.value) || 1500 })}
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <div className="flex items-center gap-1 pt-0.5">
                    {[1200, 1500, 1800, 2000, 2400].map(h => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => updatePalletType(currentPallet.id, { height: h })}
                        className={`flex-1 py-1 rounded text-[10px] font-semibold border transition-all ${
                          (currentPallet.height || 1500) === h
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {(h / 1000).toFixed(1)}m
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-gray-500 block leading-relaxed">
                    วัดจากพื้นถึงยอดบนสุดของกล่องสินค้า (ใช้คำนวณระยะห่างชั้นแร็คและระยะยกของรถยก)
                  </span>
                </div>

                {/* 5. Overhang & Max Weight */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                      ระยะ Overhang (mm)
                    </label>
                    <input 
                      type="number" min="0" max="200" step="10"
                      value={currentPallet.overhang || 0}
                      onChange={e => updatePalletType(currentPallet.id, { overhang: parseInt(e.target.value) || 0 })}
                      className="w-full p-2 border rounded-lg text-xs font-bold text-gray-900 outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <span className="text-[9px] text-gray-400 block mt-0.5">สินค้ายื่นล้นขอบพาเลท</span>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                      น้ำหนักสูงสุด (kg)
                    </label>
                    <input 
                      type="number" min="100" max="3000" step="50"
                      value={currentPallet.maxWeight || 1000}
                      onChange={e => updatePalletType(currentPallet.id, { maxWeight: parseInt(e.target.value) || 1000 })}
                      className="w-full p-2 border rounded-lg text-xs font-bold text-gray-900 outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <span className="text-[9px] text-gray-400 block mt-0.5">น้ำหนักสินค้ารวมพาเลท</span>
                  </div>
                </div>

                {/* 6. Load Summary Card */}
                <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 text-center space-y-1">
                  <div className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider">
                    สรุปมิติสินค้าต่อ 1 พาเลท
                  </div>
                  <div className="text-base font-extrabold text-gray-900">
                    {currentPallet.width} × {currentPallet.length} × {currentPallet.height || 1500} mm
                  </div>
                  <div className="text-[11px] text-gray-600 font-medium">
                    น้ำหนัก: <strong>{(currentPallet.maxWeight || 1000).toLocaleString()} kg</strong> | Overhang: <strong>{currentPallet.overhang || 0} mm</strong>
                  </div>
                  <div className="text-[10px] text-gray-500 pt-1 border-t border-emerald-200/50 flex items-center justify-center gap-3">
                    <span>ปริมาตร: <strong>{(((currentPallet.width * currentPallet.length * (currentPallet.height || 1500))) / 1e9).toFixed(2)} m³</strong></span>
                    <span>•</span>
                    <span>ประเภท: <strong>{currentPallet.entryType === 'open' ? 'พาเลทเปิด' : 'พาเลทปิด'}</strong></span>
                  </div>
                </div>

                {/* Navigation: Advance to Step 3 / Back to Step 1 */}
                <div className="pt-3 border-t border-slate-200/80 space-y-2">
                  <button
                    type="button"
                    onClick={() => switchToSection('racking')}
                    className="w-full py-2.5 px-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                  >
                    <span>ต่อไป: ออกแบบแร็ค & ระบบจัดเก็บ (ขั้นตอนที่ 3)</span>
                    <ArrowRight size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => switchToSection('layout')}
                    className="w-full py-1.5 px-3 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                  >
                    <ArrowLeft size={12} />
                    <span>ย้อนกลับไปผังอาคาร (ขั้นตอนที่ 1)</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── 3. Storage & Racking Systems (Porsche Visual Option Cards) ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.racking ? 'border-red-300 shadow-sm ring-1 ring-red-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('racking'); setActiveStep(4); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.racking ? 'bg-gradient-to-r from-red-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 border border-red-100 flex items-center justify-center shrink-0">
                <Box size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">3. รูปแบบแร็ค, ระบบจัดเก็บ & Area Design</span>
                  <span className="text-[10px] font-semibold bg-red-50 text-red-700 px-1.5 py-0.5 rounded border border-red-200">
                    {rackAreas.length} โซน
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {RACK_OPTIONS.find(r => r.type === activeRackType)?.name || 'Selective Rack'} | {(mapData.areas?.filter(a => a.type === 'model_3d').length || 0) > 0 ? `${mapData.areas.filter(a => a.type === 'model_3d').length} วัตถุ 3D | ` : ''}{totalPallets.toLocaleString()} พาเลท
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.racking ? 'bg-red-100 text-red-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.racking ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.racking && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3 animate-in fade-in duration-150">
              <p className="text-[11px] text-gray-500">
                คลิกเลือกระบบแร็ค แล้วลากวาดสี่เหลี่ยมบนผังเพื่อวางแร็ค:
              </p>

              {/* Porsche Style Visual Cards Grid with Industrial 3D Studio Renders */}
              <div className="grid grid-cols-1 gap-2.5">
                {RACK_OPTIONS.map(opt => {
                  const isSelected = activeRackType === opt.type && (mode === 'draw_area' || selectedArea?.type === opt.type);
                  return (
                    <div 
                      key={opt.type}
                      onClick={() => handleSelectRackType(opt)}
                      className={`relative rounded-2xl border transition-all duration-200 cursor-pointer overflow-hidden group ${
                        isSelected 
                          ? 'border-2 border-red-600 bg-white shadow-sm ring-1 ring-red-100' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-xs bg-white'
                      }`}
                    >
                      {/* Photo / 3D Studio Render Banner */}
                      <div className="relative w-full h-28 bg-gradient-to-b from-slate-50 to-slate-100 overflow-hidden border-b border-slate-100">
                        <img 
                          src={opt.image} 
                          alt={opt.name} 
                          className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />

                        {/* Category badge */}
                        <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-xs text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs border border-slate-200/80 flex items-center gap-1.5 text-slate-800">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                          <span>{opt.badge}</span>
                        </div>

                        {/* Selected Indicator Pill on Image */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1">
                            <Check size={11} strokeWidth={3} />
                            <span>เลือกแล้ว</span>
                          </div>
                        )}
                      </div>

                      {/* Content Details */}
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-extrabold text-gray-900 text-xs tracking-tight">{opt.name}</span>
                            </div>
                            <div className="text-[11px] font-semibold text-slate-700 mt-0.5">{opt.thai}</div>
                            <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">{opt.desc}</p>
                          </div>

                          {/* Porsche Radio Circle */}
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                            isSelected ? 'border-red-600 bg-red-50' : 'border-gray-300 bg-white group-hover:border-gray-400'
                          }`}>
                            {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Toolbar for Racks */}
              <div className="flex items-center gap-1.5 pt-1">
                <button
                  onClick={() => { setMode('draw_area'); setSelectedArea(null); setActiveStep(4); }}
                  className={`flex-1 py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    mode === 'draw_area' ? 'bg-red-600 text-white shadow-xs' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Box size={14} /> วาดแร็ค
                </button>
                <button
                  onClick={() => { setMode('select'); setActiveStep(4); }}
                  className={`py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    mode === 'select' && !selectedCorridor ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <MousePointer2 size={14} /> เลือก
                </button>
                {pasteSelected && (
                  <button
                    type="button"
                    onClick={pasteSelected}
                    className="py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1 transition-colors cursor-pointer bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                    title="วางสิ่งที่คัดลอก (Ctrl+V)"
                  >
                    <ClipboardPaste size={13} /> วาง
                  </button>
                )}
              </div>

              {/* Selected Area Configuration Drawer */}
              {selectedArea && (selectedArea.type?.startsWith('storage') || selectedArea.type === 'conveyor' || selectedArea.type === 'model_3d') && (
                <div id="selected-area-drawer" className="bg-white p-3.5 rounded-xl border-2 border-red-300 shadow-sm space-y-3 mt-3 animate-in fade-in duration-100">
                  <div className="flex items-center justify-between border-b pb-2 gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 shrink-0 bg-slate-50">
                        <img 
                          src={RACK_OPTIONS.find(r => r.type === selectedArea.type)?.image || '/images/racks/selective_rack.jpg'} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="font-bold text-gray-900 text-xs block truncate">
                          ปรับแต่ง: {selectedArea.label || 'แร็คที่เลือก'}
                        </span>
                        <span className="text-[10px] text-gray-400 block truncate">
                          {RACK_OPTIONS.find(r => r.type === selectedArea.type)?.name || selectedArea.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {copySelected && (
                        <button
                          type="button"
                          onClick={copySelected}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                          title="คัดลอกแร็คนี้ (Ctrl+C)"
                        >
                          <Copy size={12} /> คัดลอก
                        </button>
                      )}
                      {duplicateSelected && (
                        <button
                          type="button"
                          onClick={duplicateSelected}
                          className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                          title="ทำซ้ำแร็คนี้ (Ctrl+D)"
                        >
                          <Copy size={12} /> ทำซ้ำ
                        </button>
                      )}
                      <button 
                        onClick={deleteArea} 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                        title="ลบแร็คนี้"
                      >
                        <Trash2 size={12} /> ลบ
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block text-gray-600 mb-1 font-semibold text-[11px]">ชื่อเรียก (Label)</label>
                      <input 
                        type="text" 
                        value={selectedArea.label || ''} 
                        onChange={e => updateAreaProp('label', e.target.value)}
                        placeholder="เช่น Selective Rack โซน A"
                        className="w-full p-2 border rounded-lg outline-none focus:ring-1 focus:ring-red-500" 
                      />
                    </div>

                    {/* 1. Dimensions & Area Settings (ใส่ขนาดพื้นที่ กว้าง x ยาว x ตร.ม. ให้เป๊ะตามจริง) */}
                    <div className="bg-blue-50/60 border border-blue-200/80 rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-blue-900 text-xs flex items-center gap-1.5">
                          <Ruler size={13} className="text-blue-600" />
                          ขนาดโซน & พื้นที่ (Zone Dimensions & Area)
                        </span>
                        <span className="text-[10px] bg-blue-100 text-blue-800 font-semibold px-1.5 py-0.5 rounded">
                          {(parseFloat(dimState.area) || 0).toLocaleString()} m²
                        </span>
                      </div>
                      <p className="text-[10px] text-blue-700/80 leading-relaxed">
                        พิมพ์ระบุขนาด กว้าง × ยาว เพื่อให้พื้นที่แร็คตรงตามขนาดจริงของอาคาร:
                      </p>
                      <div className="grid grid-cols-3 gap-2 pt-1">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-700 mb-1">กว้าง W (m)</label>
                          <input 
                            type="number" step="0.5" min="1"
                            value={dimState.w} 
                            onChange={e => handleWidthChange(e.target.value)}
                            className="w-full p-1.5 bg-white border border-blue-300 rounded-lg text-xs font-bold text-blue-950 focus:ring-2 focus:ring-blue-500 outline-none" 
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-700 mb-1">ยาว/ลึก L (m)</label>
                          <input 
                            type="number" step="0.5" min="1"
                            value={dimState.l} 
                            onChange={e => handleLengthChange(e.target.value)}
                            className="w-full p-1.5 bg-white border border-blue-300 rounded-lg text-xs font-bold text-blue-950 focus:ring-2 focus:ring-blue-500 outline-none" 
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-700 mb-1">พื้นที่ (m²)</label>
                          <input 
                            type="number" step="1" min="1"
                            value={dimState.area} 
                            onChange={e => handleAreaChange(e.target.value)}
                            className="w-full p-1.5 bg-white border border-blue-300 rounded-lg text-xs font-bold text-blue-950 focus:ring-2 focus:ring-blue-500 outline-none" 
                          />
                        </div>
                      </div>
                    </div>

                    {selectedArea.type === 'conveyor' ? (
                      <div className="space-y-3.5 pt-1">
                        {/* 1. Conveyor Height Adjustment */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                              <Box size={13} className="text-slate-600" />
                              ความสูงสายพานลำเลียง (Conveyor Height)
                            </span>
                            <span className="text-xs font-black text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">
                              {((selectedArea.conveyorConfig?.heightM ?? 0.62)).toFixed(2)} ม.
                            </span>
                          </div>

                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <input 
                                type="number" 
                                step="0.05" 
                                min="0.2" 
                                max="3.0"
                                value={selectedArea.conveyorConfig?.heightM ?? 0.62} 
                                onChange={e => {
                                  const val = parseFloat(e.target.value) || 0.62;
                                  updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    heightM: val
                                  });
                                }}
                                className="w-24 p-2 border rounded-lg outline-none font-bold text-gray-800 text-xs focus:ring-1 focus:ring-blue-500 bg-white" 
                              />
                              <input 
                                type="range" 
                                min="0.3" 
                                max="2.0" 
                                step="0.05"
                                value={selectedArea.conveyorConfig?.heightM ?? 0.62} 
                                onChange={e => {
                                  const val = parseFloat(e.target.value) || 0.62;
                                  updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    heightM: val
                                  });
                                }}
                                className="flex-1 accent-blue-600 cursor-pointer"
                              />
                            </div>
                            
                            {/* Quick Presets */}
                            <div className="flex flex-wrap items-center gap-1.5 pt-1">
                              {[
                                { label: 'ระดับพื้น (0.35m)', val: 0.35 },
                                { label: 'มาตรฐาน (0.62m)', val: 0.62 },
                                { label: 'ระดับเอว (0.85m)', val: 0.85 },
                                { label: 'ระดับสูง (1.20m)', val: 1.20 }
                              ].map(p => (
                                <button
                                  key={p.val}
                                  type="button"
                                  onClick={() => updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    heightM: p.val
                                  })}
                                  className={`text-[10px] px-2 py-1 rounded-md border font-medium transition-colors ${
                                    (selectedArea.conveyorConfig?.heightM ?? 0.62) === p.val
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                  }`}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* 2. Pallet Flow Orientation */}
                        {(() => {
                          const defaultPallet = mapData.palletTypes?.find(p => p.isPrimary) || mapData.palletTypes?.[0] || mapData.palletSize || { width: 1000, length: 1200 };
                          const pw = defaultPallet.width || 1000;
                          const pl = defaultPallet.length || 1200;
                          let palletTotalHMm = 1500;
                          if (defaultPallet.totalHeight && defaultPallet.totalHeight > 250) palletTotalHMm = defaultPallet.totalHeight;
                          else if (defaultPallet.height && defaultPallet.height > 250) palletTotalHMm = defaultPallet.height;
                          else if (mapData.palletSize?.totalHeight && mapData.palletSize.totalHeight > 250) palletTotalHMm = mapData.palletSize.totalHeight;
                          else if (mapData.palletSize?.height && mapData.palletSize.height > 250) palletTotalHMm = mapData.palletSize.height;

                          const currentOri = selectedArea.conveyorConfig?.palletOrientation || 'auto';

                          return (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                                  <ArrowRightLeft size={13} className="text-slate-600" />
                                  แนวการไหลของพาเลท (Pallet Flow Orientation)
                                </span>
                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                                  สูง {palletTotalHMm} mm
                                </span>
                              </div>
                              <div className="grid grid-cols-3 gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    palletOrientation: 'auto'
                                  })}
                                  className={`py-2 px-1.5 rounded-lg text-xs font-bold border transition-all text-center ${
                                    currentOri === 'auto'
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  อัตโนมัติ
                                  <div className="text-[9px] font-normal opacity-80">Auto Fit</div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    palletOrientation: 'width_leading'
                                  })}
                                  className={`py-2 px-1.5 rounded-lg text-xs font-bold border transition-all text-center ${
                                    currentOri === 'width_leading'
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  หน้ากว้างนำ
                                  <div className="text-[9px] font-normal opacity-80">{Math.min(pw, pl)} mm นำ</div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => updateAreaProp('conveyorConfig', {
                                    ...selectedArea.conveyorConfig,
                                    palletOrientation: 'length_leading'
                                  })}
                                  className={`py-2 px-1.5 rounded-lg text-xs font-bold border transition-all text-center ${
                                    currentOri === 'length_leading'
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  หน้ายาวนำ
                                  <div className="text-[9px] font-normal opacity-80">{Math.max(pw, pl)} mm นำ</div>
                                </button>
                              </div>
                              <p className="text-[10px] text-gray-500 leading-normal">
                                {currentOri === 'width_leading'
                                  ? `ไหลโดยหันด้าน ${Math.min(pw, pl)} มม. ไปตามแนวสายพาน (สินค้าสูง ${palletTotalHMm} มม.)`
                                  : currentOri === 'length_leading'
                                  ? `ไหลโดยหันด้าน ${Math.max(pw, pl)} มม. ไปตามแนวสายพาน (สินค้าสูง ${palletTotalHMm} มม.)`
                                  : `คำนวณด้านที่เหมาะสมกับความกว้างสายพานที่วาดอัตโนมัติ (สินค้าสูง ${palletTotalHMm} มม.)`}
                              </p>
                            </div>
                          );
                        })()}

                        {/* 3. Side Gap / Clearance Adjustment */}
                        {(() => {
                          const sideGapMm = selectedArea.conveyorConfig?.sideGapMm !== undefined 
                            ? selectedArea.conveyorConfig.sideGapMm 
                            : 50;

                          const defaultPallet = mapData.palletTypes?.find(p => p.isPrimary) || mapData.palletTypes?.[0] || mapData.palletSize || { width: 1000, length: 1200 };
                          const pw = defaultPallet.width || 1000;
                          const pl = defaultPallet.length || 1200;
                          const currentOri = selectedArea.conveyorConfig?.palletOrientation || 'auto';

                          const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
                          const isHoriz = selectedArea.w >= selectedArea.h;
                          const currentConvWidthMm = (isHoriz ? selectedArea.h : selectedArea.w) / ppm;

                          let nominalPalletCrossMm = 1000;
                          if (currentOri === 'width_leading') {
                            nominalPalletCrossMm = Math.max(pw, pl);
                          } else if (currentOri === 'length_leading') {
                            nominalPalletCrossMm = Math.min(pw, pl);
                          } else {
                            nominalPalletCrossMm = currentConvWidthMm >= (pl + sideGapMm * 2) ? pl : pw;
                          }

                          // Target conveyor width to fit pallet + 2 * sideGap
                          const fitWidthMm = Math.round(nominalPalletCrossMm + (sideGapMm * 2));
                          const fitWidthM = (fitWidthMm / 1000).toFixed(2);
                          const currentWidthM = (currentConvWidthMm / 1000).toFixed(2);

                          const handleSideGapChange = (newVal, forceSync = false) => {
                            const val = Math.max(0, Math.min(300, parseInt(newVal) || 0));
                            const newConfig = {
                              ...selectedArea.conveyorConfig,
                              sideGapMm: val
                            };

                            const shouldSync = forceSync || selectedArea.conveyorConfig?.autoFitWidth;
                            if (shouldSync) {
                              const targetWidthMm = nominalPalletCrossMm + (val * 2);
                              const targetPx = targetWidthMm * ppm;
                              if (isHoriz) {
                                updateAreaProp({
                                  conveyorConfig: newConfig,
                                  h: targetPx
                                });
                              } else {
                                updateAreaProp({
                                  conveyorConfig: newConfig,
                                  w: targetPx
                                });
                              }
                            } else {
                              updateAreaProp('conveyorConfig', newConfig);
                            }
                          };

                          const applyFitWidth = () => {
                            const targetPx = fitWidthMm * ppm;
                            if (isHoriz) {
                              updateAreaProp('h', targetPx);
                            } else {
                              updateAreaProp('w', targetPx);
                            }
                          };

                          return (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                                  <Maximize2 size={13} className="text-slate-600" />
                                  ระยะห่างขอบสายพาน (Side Gap / Clearance)
                                </span>
                                <span className="text-xs font-black text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">
                                  {sideGapMm} มม.
                                </span>
                              </div>

                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="relative w-24">
                                    <input 
                                      type="number" 
                                      step="5" 
                                      min="0" 
                                      max="250"
                                      value={sideGapMm} 
                                      onChange={e => handleSideGapChange(e.target.value)}
                                      className="w-full p-2 pr-8 border rounded-lg outline-none font-bold text-gray-800 text-xs focus:ring-1 focus:ring-blue-500 bg-white" 
                                    />
                                    <span className="absolute right-2 top-2 text-[10px] text-gray-400 font-semibold pointer-events-none">มม.</span>
                                  </div>
                                  <input 
                                    type="range" 
                                    min="0" 
                                    max="200" 
                                    step="5"
                                    value={sideGapMm} 
                                    onChange={e => handleSideGapChange(e.target.value)}
                                    className="flex-1 accent-blue-600 cursor-pointer"
                                  />
                                </div>

                                {/* Quick Presets */}
                                <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                                  {[
                                    { label: '0 mm (ชิดขอบ)', val: 0 },
                                    { label: '30 mm (แคบ)', val: 30 },
                                    { label: '50 mm (มาตรฐาน)', val: 50 },
                                    { label: '80 mm (ปลอดภัย)', val: 80 },
                                    { label: '100 mm (กว้าง)', val: 100 }
                                  ].map(p => (
                                    <button
                                      key={p.val}
                                      type="button"
                                      onClick={() => handleSideGapChange(p.val)}
                                      className={`text-[10px] px-2 py-1 rounded-md border font-medium transition-colors ${
                                        sideGapMm === p.val
                                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                      }`}
                                    >
                                      {p.label}
                                    </button>
                                  ))}
                                </div>

                                {/* Width alignment info & Auto-fit button */}
                                <div className="mt-2 pt-2 border-t border-slate-200/90 space-y-1.5">
                                  <div className="flex items-center justify-between text-[11px] text-slate-600">
                                    <span>ขนาดความกว้างสายพานปัจจุบัน:</span>
                                    <span className="font-bold text-slate-800">{currentWidthM} ม. ({Math.round(currentConvWidthMm)} มม.)</span>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] text-slate-600">
                                    <span>ขนาดพาเลทด้านขวาง:</span>
                                    <span className="font-bold text-slate-800">{nominalPalletCrossMm} มม.</span>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] text-slate-600">
                                    <span>ระยะห่าง 2 ข้างรวม:</span>
                                    <span className="font-bold text-blue-600">+{sideGapMm * 2} มม. (ข้างละ {sideGapMm} มม.)</span>
                                  </div>

                                  <div className="pt-1">
                                    <button
                                      type="button"
                                      onClick={applyFitWidth}
                                      className="w-full py-1.5 px-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                                    >
                                      <Maximize2 size={12} />
                                      ปรับความกว้างสายพานให้พอดี ({fitWidthM} ม.)
                                    </button>
                                  </div>

                                  <label className="flex items-center gap-2 text-[10px] text-slate-600 cursor-pointer pt-0.5 select-none">
                                    <input 
                                      type="checkbox"
                                      checked={!!selectedArea.conveyorConfig?.autoFitWidth}
                                      onChange={e => {
                                        const auto = e.target.checked;
                                        const newConfig = {
                                          ...selectedArea.conveyorConfig,
                                          autoFitWidth: auto
                                        };
                                        if (auto) {
                                          const targetPx = fitWidthMm * ppm;
                                          if (isHoriz) {
                                            updateAreaProp({ conveyorConfig: newConfig, h: targetPx });
                                          } else {
                                            updateAreaProp({ conveyorConfig: newConfig, w: targetPx });
                                          }
                                        } else {
                                          updateAreaProp('conveyorConfig', newConfig);
                                        }
                                      }}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                                    />
                                    <span>ปรับความกว้างสายพานตามระยะห่างอัตโนมัติ (Auto-sync width)</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* 4. Operator Workstation Simulation */}
                        <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-3 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-amber-900 text-xs flex items-center gap-1.5">
                              <Users size={14} className="text-amber-700" />
                              จำลองคนยืนปฏิบัติงาน (Operator Station)
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input 
                                type="checkbox"
                                checked={!!selectedArea.conveyorConfig?.operatorsEnabled}
                                onChange={e => updateAreaProp('conveyorConfig', {
                                  ...selectedArea.conveyorConfig,
                                  operatorsEnabled: e.target.checked
                                })}
                                className="sr-only peer"
                              />
                              <div className="w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-amber-600"></div>
                            </label>
                          </div>

                          {selectedArea.conveyorConfig?.operatorsEnabled && (
                            <div className="space-y-2.5 pt-1 border-t border-amber-200/80 animate-in fade-in duration-150">
                              {/* Operator Side */}
                              <div>
                                <label className="block text-[11px] font-semibold text-amber-900 mb-1">ฝั่งตำแหน่งคนยืน</label>
                                <div className="grid grid-cols-3 gap-1">
                                  {[
                                    { id: 'left', label: 'ฝั่งซ้าย / บน' },
                                    { id: 'right', label: 'ฝั่งขวา / ล่าง' },
                                    { id: 'both', label: 'ทั้ง 2 ฝั่ง' }
                                  ].map(s => (
                                    <button
                                      key={s.id}
                                      type="button"
                                      onClick={() => updateAreaProp('conveyorConfig', {
                                        ...selectedArea.conveyorConfig,
                                        operatorSide: s.id
                                      })}
                                      className={`py-1.5 px-1 rounded-lg text-[11px] font-bold border transition-all ${
                                        (selectedArea.conveyorConfig?.operatorSide || 'right') === s.id
                                          ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                                          : 'bg-white text-gray-700 border-amber-200 hover:bg-amber-100/50'
                                      }`}
                                    >
                                      {s.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Operator Count */}
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[11px] font-semibold text-amber-900 mb-1">จำนวนคนทำงาน</label>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4].map(num => (
                                      <button
                                        key={num}
                                        type="button"
                                        onClick={() => updateAreaProp('conveyorConfig', {
                                          ...selectedArea.conveyorConfig,
                                          operatorCount: num
                                        })}
                                        className={`flex-1 py-1 rounded-lg text-xs font-bold border ${
                                          (selectedArea.conveyorConfig?.operatorCount || 1) === num
                                            ? 'bg-amber-600 text-white border-amber-600'
                                            : 'bg-white text-gray-700 border-amber-200 hover:bg-amber-100/50'
                                        }`}
                                      >
                                        {num}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-[11px] font-semibold text-amber-900 mb-1">หน้าที่ / ลักษณะงาน</label>
                                  <select
                                    value={selectedArea.conveyorConfig?.operatorRole || 'picking'}
                                    onChange={e => updateAreaProp('conveyorConfig', {
                                      ...selectedArea.conveyorConfig,
                                      operatorRole: e.target.value
                                    })}
                                    className="w-full p-1.5 bg-white border border-amber-200 rounded-lg text-xs font-semibold text-gray-800 outline-none"
                                  >
                                    <option value="picking">หยิบ/คัดแยก (Picking)</option>
                                    <option value="packing">แพ็คกิ้ง (Packing)</option>
                                    <option value="qc">ตรวจสอบคุณภาพ (QC)</option>
                                    <option value="loading">นำขึ้น/ลง (Loading)</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* 5. Conveyor Pallet Load Status (Empty / Full Pallet Selection) */}
                        {(() => {
                          const c = getPalletCapacity(selectedArea);
                          const totalPallets = c.total || 0;
                          const emptySlots = (selectedArea.conveyorConfig?.emptySlots || []).filter(idx => idx < totalPallets);
                          const emptyCount = emptySlots.length;
                          const fullCount = Math.max(0, totalPallets - emptyCount);

                          const toggleSlot = (idx) => {
                            let newSlots;
                            if (emptySlots.includes(idx)) {
                              newSlots = emptySlots.filter(s => s !== idx);
                            } else {
                              newSlots = [...emptySlots, idx].sort((a, b) => a - b);
                            }
                            updateAreaProp('conveyorConfig', {
                              ...selectedArea.conveyorConfig,
                              emptySlots: newSlots
                            });
                          };

                          const setAllFull = () => {
                            updateAreaProp('conveyorConfig', {
                              ...selectedArea.conveyorConfig,
                              emptySlots: []
                            });
                          };

                          const setAllEmpty = () => {
                            updateAreaProp('conveyorConfig', {
                              ...selectedArea.conveyorConfig,
                              emptySlots: Array.from({ length: totalPallets }, (_, i) => i)
                            });
                          };

                          const setAlternate = () => {
                            const newSlots = [];
                            for (let i = 1; i < totalPallets; i += 2) {
                              newSlots.push(i);
                            }
                            updateAreaProp('conveyorConfig', {
                              ...selectedArea.conveyorConfig,
                              emptySlots: newSlots
                            });
                          };

                          return (
                            <div className="bg-amber-50/50 border border-amber-200/90 rounded-xl p-3 space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-amber-950 text-xs flex items-center gap-1.5">
                                  <Package size={13} className="text-amber-700" />
                                  กำหนดพาเลทเต็ม / พาเลทเปล่า (Pallet Status)
                                </span>
                                <div className="flex items-center gap-1.5 text-[10px]">
                                  <span className="font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-md border border-emerald-200">
                                    เต็ม {fullCount}
                                  </span>
                                  <span className="font-bold text-amber-800 bg-amber-100/90 px-2 py-0.5 rounded-md border border-amber-300">
                                    เปล่า {emptyCount}
                                  </span>
                                </div>
                              </div>

                              <p className="text-[11px] text-amber-900/80">
                                คลิกที่ช่องพาเลทเพื่อสลับระหว่าง <strong>พาเลทเต็มสินค้า 📦</strong> และ <strong>พาเลทเปล่า 🪵</strong>
                              </p>

                              {/* Quick Presets */}
                              <div className="flex flex-wrap items-center gap-1 pt-0.5">
                                <button
                                  type="button"
                                  onClick={setAllFull}
                                  className={`text-[10px] px-2 py-1 rounded-md font-semibold border transition-all ${
                                    emptyCount === 0
                                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-emerald-50'
                                  }`}
                                >
                                  📦 เต็มทั้งหมด
                                </button>
                                <button
                                  type="button"
                                  onClick={setAllEmpty}
                                  className={`text-[10px] px-2 py-1 rounded-md font-semibold border transition-all ${
                                    emptyCount === totalPallets && totalPallets > 0
                                      ? 'bg-amber-700 text-white border-amber-700 shadow-xs'
                                      : 'bg-white text-gray-700 border-gray-200 hover:bg-amber-50'
                                  }`}
                                >
                                  🪵 เปล่าทั้งหมด
                                </button>
                                <button
                                  type="button"
                                  onClick={setAlternate}
                                  className="text-[10px] px-2 py-1 rounded-md font-semibold border bg-white text-gray-700 border-gray-200 hover:bg-gray-100 transition-all"
                                >
                                  🔄 สลับเต็ม-เปล่า
                                </button>
                              </div>

                              {/* Interactive Pallet Slot Chips */}
                              {totalPallets > 0 ? (
                                <div className="pt-1.5 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                                  {Array.from({ length: totalPallets }).map((_, idx) => {
                                    const isEmpty = emptySlots.includes(idx);
                                    return (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => toggleSlot(idx)}
                                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs ${
                                          isEmpty
                                            ? 'bg-amber-100/90 text-amber-950 border-amber-400 hover:bg-amber-200'
                                            : 'bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100'
                                        }`}
                                        title={`คลิกเพื่อเปลี่ยนเป็น ${isEmpty ? 'พาเลทเต็ม' : 'พาเลทเปล่า'}`}
                                      >
                                        <span>{isEmpty ? '🪵' : '📦'}</span>
                                        <span>ช่องที่ {idx + 1}</span>
                                        <span className={`text-[9px] px-1 py-0.2 rounded font-black ${
                                          isEmpty ? 'bg-amber-300/80 text-amber-900' : 'bg-emerald-200 text-emerald-800'
                                        }`}>
                                          {isEmpty ? 'เปล่า' : 'เต็ม'}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-[11px] text-gray-400 italic py-1">
                                  ขยายความยาวสายพานเพื่อวางพาเลท
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Pallet & Operator summary badge */}
                        {(() => {
                          const c = getPalletCapacity(selectedArea);
                          const opCount = selectedArea.conveyorConfig?.operatorsEnabled 
                            ? ((selectedArea.conveyorConfig?.operatorSide === 'both' ? 2 : 1) * (selectedArea.conveyorConfig?.operatorCount || 1))
                            : 0;

                          return (
                            <div className="bg-slate-100 border border-slate-300/80 rounded-xl p-3 text-center space-y-1">
                              <div className="flex items-center justify-around divide-x divide-slate-300">
                                <div>
                                  <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider">ความจุพาเลท</div>
                                  <div className="text-xl font-black text-gray-900">~{c.total.toLocaleString()} พาเลท</div>
                                </div>
                                {opCount > 0 && (
                                  <div className="pl-4">
                                    <div className="text-[10px] text-amber-700 font-bold uppercase tracking-wider">คนปฏิบัติงาน</div>
                                    <div className="text-xl font-black text-amber-800">{opCount} คน</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : selectedArea.type === 'model_3d' ? (
                      <div className="space-y-3.5 pt-1">
                        {/* 3D Model Selection Card */}
                        <div className="bg-purple-50/70 border border-purple-200 rounded-xl p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-purple-900 text-xs flex items-center gap-1.5">
                              <Box size={14} className="text-purple-600" />
                              เลือกโมเดล 3D ที่จะนำมาแสดง (3D Asset)
                            </span>
                            <span className="text-[10px] bg-purple-200/60 text-purple-900 font-extrabold px-2 py-0.5 rounded-full">
                              3D Simulation
                            </span>
                          </div>

                          {/* Model Select Options */}
                          <div>
                            <label className="block text-[11px] font-semibold text-purple-950 mb-1">
                              เลือกโมเดลจากคลังโมเดลที่บันทึกไว้:
                            </label>
                            {available3DModels.length === 0 ? (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs space-y-1.5">
                                <div className="font-bold flex items-center gap-1.5">
                                  <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                                  <span>ยังไม่มีโมเดล 3D ที่บันทึกไว้ในระบบ</span>
                                </div>
                                <p className="text-[11px] text-amber-700 leading-relaxed">
                                  กรุณาไปที่เมนู <strong>Admin &gt; จัดการโมเดล 3D</strong> เพื่ออัปโหลดไฟล์โมเดล (.glb) เข้าสู่ระบบก่อน
                                </p>
                              </div>
                            ) : (
                              <select
                                value={
                                  selectedArea.modelConfig?.modelId ||
                                  String(available3DModels.find(m => m.file_url === selectedArea.modelConfig?.fileUrl || m.name === (selectedArea.modelConfig?.modelName || selectedArea.label))?.id || available3DModels[0]?.id || '')
                                }
                                onChange={e => {
                                  const val = e.target.value;
                                  const custom = available3DModels.find(m => String(m.id) === val);
                                  if (custom) {
                                    updateAreaProp({
                                      label: custom.name,
                                      modelConfig: {
                                        ...selectedArea.modelConfig,
                                        modelId: String(custom.id),
                                        modelName: custom.name,
                                        category: custom.category,
                                        fileUrl: custom.file_url,
                                        targetHeight: custom.target_height_m || 1.70,
                                        rotationDeg: selectedArea.modelConfig?.rotationDeg ?? 0
                                      }
                                    });
                                  }
                                }}
                                className="w-full p-2.5 bg-white border border-purple-300 rounded-lg text-xs font-bold text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
                              >
                                {available3DModels.map(m => (
                                  <option key={m.id} value={String(m.id)}>
                                    {m.category === 'reach_truck' ? '🚜 ' : m.category === 'forklift' ? '🚚 ' : m.category === 'operator' ? '👷 ' : m.category === 'equipment' ? '🏗️ ' : '📦 '}
                                    {m.name} ({m.target_height_m}m)
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>

                          {/* Quick visual preview badge */}
                          {available3DModels.length > 0 && (
                            <div className="p-2.5 bg-white rounded-lg border border-purple-200/80 flex items-center justify-between text-xs">
                              <div>
                                <span className="text-[10px] text-gray-400 block uppercase">โมเดลที่เลือก:</span>
                                <span className="font-extrabold text-purple-900">
                                  {selectedArea.modelConfig?.modelName || available3DModels[0]?.name}
                                </span>
                              </div>
                              <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md">
                                สเกล: {selectedArea.modelConfig?.targetHeight || available3DModels[0]?.target_height_m || 1.70} ม.
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Orientation / Rotation Controls */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                              <RotateCcw size={13} className="text-slate-600" />
                              มุมหันของโมเดล (Orientation / Rotation)
                            </span>
                            <span className="text-xs font-black text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md">
                              {(selectedArea.modelConfig?.rotationDeg ?? 0)}°
                            </span>
                          </div>

                          {/* 4 Direction Presets */}
                          <div className="grid grid-cols-4 gap-1.5">
                            {[
                              { label: '0° หน้า', deg: 0 },
                              { label: '90° ขวา', deg: 90 },
                              { label: '180° หลัง', deg: 180 },
                              { label: '270° ซ้าย', deg: 270 }
                            ].map(btn => (
                              <button
                                key={btn.deg}
                                type="button"
                                onClick={() => updateAreaProp('modelConfig', {
                                  ...selectedArea.modelConfig,
                                  rotationDeg: btn.deg
                                })}
                                className={`py-1.5 px-1 rounded-lg text-xs font-bold border transition-all text-center cursor-pointer ${
                                  (selectedArea.modelConfig?.rotationDeg ?? 0) === btn.deg
                                    ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                {btn.label}
                              </button>
                            ))}
                          </div>

                          {/* Angle Slider */}
                          <div className="pt-1">
                            <input
                              type="range"
                              min="0"
                              max="360"
                              step="5"
                              value={selectedArea.modelConfig?.rotationDeg ?? 0}
                              onChange={e => updateAreaProp('modelConfig', {
                                ...selectedArea.modelConfig,
                                rotationDeg: parseInt(e.target.value, 10) || 0
                              })}
                              className="w-full accent-purple-600 cursor-pointer"
                            />
                            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                              <span>0°</span>
                              <span>90°</span>
                              <span>180°</span>
                              <span>270°</span>
                              <span>360°</span>
                            </div>
                          </div>
                        </div>

                        {/* Model Height Scale Override */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800 text-xs">
                              ปรับความสูงโมเดล (Target Height)
                            </span>
                            <span className="text-[10px] text-gray-400">
                              หน่วยเป็นเมตร (m)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.05"
                              min="0.3"
                              max="10.0"
                              value={selectedArea.modelConfig?.targetHeight ?? 1.70}
                              onChange={e => updateAreaProp('modelConfig', {
                                ...selectedArea.modelConfig,
                                targetHeight: parseFloat(e.target.value) || 1.70
                              })}
                              className="w-28 p-2 border rounded-lg text-xs font-bold text-gray-800 outline-none focus:ring-1 focus:ring-purple-500 bg-white"
                            />
                            <span className="text-xs text-gray-500">
                              ({Math.round((selectedArea.modelConfig?.targetHeight ?? 1.70) * 100)} cm)
                            </span>
                          </div>
                        </div>

                        {/* Auto-fit Footprint Button */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                              <Maximize2 size={13} className="text-purple-600" />
                              ปรับขนาดกล่องตามสัดส่วนจริง (Footprint)
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {((Math.abs(selectedArea.w) / (scaleCalibration?.pixelsPerMm || 0.0125)) / 1000).toFixed(1)}m × {((Math.abs(selectedArea.h) / (scaleCalibration?.pixelsPerMm || 0.0125)) / 1000).toFixed(1)}m
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const ppm = scaleCalibration?.pixelsPerMm || 0.0125;
                              const cfg = selectedArea.modelConfig || {};
                              const catRaw = (cfg.category || '').toLowerCase();
                              const nameLower = (cfg.modelName || selectedArea.label || '').toLowerCase();
                              const fileLower = (cfg.fileUrl || '').toLowerCase();

                              let cat = 'reach_truck';
                              if (catRaw === 'operator' || nameLower.includes('operator') || nameLower.includes('worker')) {
                                cat = 'operator';
                              } else if (catRaw === 'reach_truck' || nameLower.includes('reach') || nameLower.includes('rae') || nameLower.includes('rre') || fileLower.includes('reach')) {
                                cat = 'reach_truck';
                              } else if (catRaw === 'forklift' || nameLower.includes('forklift')) {
                                cat = 'forklift';
                              }

                              const rot = ((cfg.rotationDeg ?? 0) % 360 + 360) % 360;
                              const isRotatedSide = (Math.abs(rot - 90) < 45) || (Math.abs(rot - 270) < 45);
                              
                              let baseW_mm = 1400;
                              let baseH_mm = 2800;
                              if (cat === 'reach_truck') {
                                baseW_mm = 1400;
                                baseH_mm = 2800;
                              } else if (cat === 'forklift') {
                                baseW_mm = 1300;
                                baseH_mm = 3100;
                              } else if (cat === 'operator') {
                                baseW_mm = 1000;
                                baseH_mm = 1000;
                              }
                              
                              const realW_mm = isRotatedSide ? baseH_mm : baseW_mm;
                              const realH_mm = isRotatedSide ? baseW_mm : baseH_mm;
                              
                              const newW = realW_mm * ppm;
                              const newH = realH_mm * ppm;
                              const cx = selectedArea.x + selectedArea.w / 2;
                              const cy = selectedArea.y + selectedArea.h / 2;
                              
                              updateAreaProp({
                                x: cx - newW / 2,
                                y: cy - newH / 2,
                                w: newW,
                                h: newH
                              });
                            }}
                            className="w-full py-2 px-3 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
                          >
                            <Maximize2 size={13} />
                            <span>ปรับขนาดกล่องให้พอดีตัวโมเดล (Auto-Fit)</span>
                          </button>
                        </div>

                        {/* Admin Link Notice */}
                        <div className="p-2.5 bg-purple-50 border border-purple-100 rounded-xl text-[11px] text-purple-900 flex items-center justify-between">
                          <span>ต้องการเพิ่มโมเดล 3D .glb เอง?</span>
                          <a
                            href="/admin"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-purple-700 hover:underline flex items-center gap-1"
                          >
                            <span>หน้า Admin 3D</span>
                            <ChevronUp size={12} className="rotate-90" />
                          </a>
                        </div>
                      </div>
                    ) : (selectedArea.type === 'storage' || selectedArea.type === 'storage_on_floor') ? (
                      <div className="space-y-3.5 pt-1">
                        {(() => {
                          const c = getPalletCapacity(selectedArea);
                          const totalSlots = (c.cols || 1) * (c.rows || 1);
                          const palletStatus = selectedArea.rackConfig?.palletStatus || 'full';
                          const emptySlots = (selectedArea.rackConfig?.emptySlots || []).filter(s => s < totalSlots);
                          const levels = selectedArea.rackConfig?.levels || 1;
                          const emptyStack = selectedArea.rackConfig?.emptyPalletStackCount || (palletStatus === 'empty' ? levels : 10);

                          const isAllFull = palletStatus === 'full';
                          const isAllEmpty = palletStatus === 'empty';
                          const isCustom = palletStatus === 'custom';

                          const customEmptyCount = isCustom ? emptySlots.length : (isAllEmpty ? totalSlots : 0);
                          const customFullCount = isCustom ? Math.max(0, totalSlots - customEmptyCount) : (isAllFull ? totalSlots : 0);

                          const toggleFloorSlot = (idx) => {
                            let newSlots;
                            if (emptySlots.includes(idx)) {
                              newSlots = emptySlots.filter(s => s !== idx);
                            } else {
                              newSlots = [...emptySlots, idx].sort((a, b) => a - b);
                            }
                            updateAreaProp('rackConfig', {
                              ...selectedArea.rackConfig,
                              palletStatus: 'custom',
                              emptySlots: newSlots
                            });
                          };

                          return (
                            <>
                              {/* 1. Pallet Load Status Mode */}
                              <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-3 space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-indigo-950 text-xs flex items-center gap-1.5">
                                    <Box size={14} className="text-indigo-600" />
                                    สถานะพาเลทบนพื้น (Floor Pallet Status)
                                  </span>
                                  <span className="text-[10px] bg-indigo-200/70 text-indigo-900 font-extrabold px-2 py-0.5 rounded-full">
                                    Floor Staging
                                  </span>
                                </div>

                                {/* Mode selection buttons */}
                                <div className="grid grid-cols-3 gap-1.5">
                                  {[
                                    { id: 'full', label: '📦 เต็มทั้งหมด', desc: 'มีสินค้าทุกพาเลท' },
                                    { id: 'empty', label: '🪵 เปล่าทั้งหมด', desc: 'กองพาเลทเปล่า' },
                                    { id: 'custom', label: '🎯 กำหนดรายช่อง', desc: 'เลือกเฉพาะจุด' }
                                  ].map(item => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => updateAreaProp('rackConfig', {
                                        ...selectedArea.rackConfig,
                                        palletStatus: item.id
                                      })}
                                      className={`p-2 rounded-xl text-center border transition-all cursor-pointer ${
                                        palletStatus === item.id
                                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs ring-2 ring-indigo-200'
                                          : 'bg-white text-gray-700 border-gray-200 hover:bg-indigo-50/50'
                                      }`}
                                    >
                                      <div className="font-bold text-[11px]">{item.label}</div>
                                      <div className={`text-[9px] mt-0.5 ${palletStatus === item.id ? 'text-indigo-100' : 'text-gray-400'}`}>
                                        {item.desc}
                                      </div>
                                    </button>
                                  ))}
                                </div>

                                {/* Sub-options for Full Pallets: Stacking Levels */}
                                {(isAllFull || (isCustom && customFullCount > 0)) && (
                                  <div className="p-2.5 bg-white rounded-lg border border-indigo-100 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <label className="text-[11px] font-semibold text-gray-800">
                                        การวางซ้อนพาเลทเต็ม (Cargo Stacking Levels)
                                      </label>
                                      <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-200">
                                        {levels} ชั้น
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-1.5">
                                      {[
                                        { lvl: 1, label: '1 ชั้น (บนพื้น)' },
                                        { lvl: 2, label: '2 ชั้น (ซ้อน)' },
                                        { lvl: 3, label: '3 ชั้น (ซ้อน)' },
                                        { lvl: 4, label: '4 ชั้น' }
                                      ].map(item => (
                                        <button
                                          key={item.lvl}
                                          type="button"
                                          onClick={() => updateAreaProp('rackConfig', {
                                            ...selectedArea.rackConfig,
                                            levels: item.lvl
                                          })}
                                          className={`py-1.5 px-1 rounded-lg text-xs font-bold border transition-all ${
                                            levels === item.lvl
                                              ? 'bg-indigo-600 text-white border-indigo-600'
                                              : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                                          }`}
                                        >
                                          {item.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Sub-options for Empty Pallets: Stack Count */}
                                {(isAllEmpty || (isCustom && customEmptyCount > 0)) && (
                                  <div className="p-2.5 bg-white rounded-lg border border-amber-200 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <label className="text-[11px] font-semibold text-amber-950">
                                        จำนวนพาเลทเปล่าซ้อนต่อกอง (Empty Stack Height)
                                      </label>
                                      <span className="text-xs font-black text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded-md border border-amber-300">
                                        {emptyStack} ตัว/กอง
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-5 gap-1">
                                      {[1, 5, 10, 15, 20].map(cnt => (
                                        <button
                                          key={cnt}
                                          type="button"
                                          onClick={() => updateAreaProp('rackConfig', {
                                            ...selectedArea.rackConfig,
                                            emptyPalletStackCount: cnt
                                          })}
                                          className={`py-1 rounded-lg text-xs font-bold border transition-all ${
                                            emptyStack === cnt
                                              ? 'bg-amber-600 text-white border-amber-600'
                                              : 'bg-amber-50/50 text-gray-700 border-amber-200 hover:bg-amber-100/50'
                                          }`}
                                        >
                                          {cnt === 1 ? '1 ตัว' : `${cnt} ตัว`}
                                        </button>
                                      ))}
                                    </div>
                                    <div className="flex items-center gap-2 pt-1">
                                      <span className="text-[11px] text-gray-500">กำหนดเอง:</span>
                                      <input
                                        type="number"
                                        min="1"
                                        max="25"
                                        value={emptyStack}
                                        onChange={e => updateAreaProp('rackConfig', {
                                          ...selectedArea.rackConfig,
                                          emptyPalletStackCount: Math.max(1, Math.min(25, parseInt(e.target.value) || 1))
                                        })}
                                        className="w-20 p-1 border rounded text-xs font-bold text-gray-800 outline-none"
                                      />
                                      <span className="text-[11px] text-gray-500">ตัวต่อกอง (สูง ~{(emptyStack * 0.14).toFixed(2)} ม.)</span>
                                    </div>
                                  </div>
                                )}

                                {/* Custom per-slot interactive grid */}
                                {isCustom && (
                                  <div className="p-2.5 bg-white rounded-lg border border-indigo-200 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[11px] font-bold text-gray-800">
                                        ตารางเลือกตำแหน่งพาเลท ({c.cols} × {c.rows} = {totalSlots} ตำแหน่ง):
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, emptySlots: [] })}
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-100 font-medium"
                                        >
                                          เต็มหมด
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => updateAreaProp('rackConfig', {
                                            ...selectedArea.rackConfig,
                                            emptySlots: Array.from({ length: totalSlots }, (_, i) => i)
                                          })}
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-100 font-medium"
                                        >
                                          เปล่าหมด
                                        </button>
                                      </div>
                                    </div>

                                    <div 
                                      className="grid gap-1.5 max-h-48 overflow-y-auto p-1 bg-slate-50 rounded-lg border border-slate-200"
                                      style={{ gridTemplateColumns: `repeat(${Math.min(c.cols || 1, 6)}, minmax(0, 1fr))` }}
                                    >
                                      {Array.from({ length: totalSlots }).map((_, idx) => {
                                        const isEmpty = emptySlots.includes(idx);
                                        const row = Math.floor(idx / (c.cols || 1)) + 1;
                                        const col = (idx % (c.cols || 1)) + 1;
                                        return (
                                          <button
                                            key={idx}
                                            type="button"
                                            onClick={() => toggleFloorSlot(idx)}
                                            className={`p-1.5 rounded-lg text-center border transition-all cursor-pointer ${
                                              isEmpty
                                                ? 'bg-amber-100 text-amber-900 border-amber-400 font-bold shadow-2xs'
                                                : 'bg-emerald-50 text-emerald-900 border-emerald-300 font-bold'
                                            }`}
                                            title={`แถว ${row} คอลัมน์ ${col}: คลิกเพื่อสลับ`}
                                          >
                                            <div className="text-sm leading-none">{isEmpty ? '🪵' : '📦'}</div>
                                            <div className="text-[9px] mt-0.5 opacity-80">R{row}C{col}</div>
                                            <div className="text-[8px] font-black">{isEmpty ? 'เปล่า' : 'เต็ม'}</div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* 2. Orientation Card */}
                              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                                <label className="block text-gray-700 font-bold text-xs">
                                  ทิศทางการวางพาเลท (Orientation)
                                </label>
                                <div className="grid grid-cols-3 gap-1.5">
                                  {[
                                    { id: 'auto', label: 'Auto (อัตโนมัติ)' },
                                    { id: 'horizontal', label: 'แนวนอน' },
                                    { id: 'vertical', label: 'แนวตั้ง' }
                                  ].map(item => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => updateAreaProp('rackConfig', {
                                        ...selectedArea.rackConfig,
                                        orientation: item.id
                                      })}
                                      className={`py-1.5 px-2 rounded-lg text-xs font-semibold border transition-all ${
                                        (selectedArea.rackConfig?.orientation || 'auto') === item.id
                                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                                      }`}
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* 3. Summary Card */}
                              <div className="bg-slate-100 border border-slate-300/80 rounded-xl p-3 space-y-2">
                                <div className="flex items-center justify-around divide-x divide-slate-300 text-center">
                                  <div>
                                    <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider">ตำแหน่งวางบนพื้น</div>
                                    <div className="text-lg font-black text-gray-900">{c.cols} × {c.rows} ({totalSlots} กอง)</div>
                                  </div>
                                  <div className="pl-4">
                                    <div className="text-[10px] text-indigo-700 font-bold uppercase tracking-wider">ความจุพาเลททั้งหมด</div>
                                    <div className="text-lg font-black text-indigo-900">
                                      {isCustom 
                                        ? `~${(customFullCount * levels + customEmptyCount * emptyStack).toLocaleString()} พาเลท`
                                        : isAllEmpty 
                                          ? `~${(totalSlots * emptyStack).toLocaleString()} พาเลทเปล่า` 
                                          : `~${(totalSlots * levels).toLocaleString()} พาเลท`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <>
                        {/* 2. Core Structure & Aisle */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-gray-600 mb-1 font-semibold text-[11px]">จำนวนชั้น (Levels)</label>
                            <input 
                              type="number" min="1" max="20" 
                              value={selectedArea.rackConfig?.levels ?? 4} 
                              onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, levels: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1) })}
                              className="w-full p-2 border rounded-lg outline-none focus:ring-1 focus:ring-red-500 font-bold text-gray-800" 
                            />
                          </div>
                          <div>
                            <label className="block text-gray-600 mb-1 font-semibold text-[11px]">ทางเดินรถ Ast (mm)</label>
                            <input 
                              type="number" min="1000" step="100" 
                              value={selectedArea.rackConfig?.aisleWidth ?? 3000} 
                              onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, aisleWidth: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) })}
                              className="w-full p-2 border rounded-lg outline-none focus:ring-1 focus:ring-red-500 font-bold text-gray-800" 
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-gray-600 mb-1 font-semibold text-[11px]">ระยะชิดหลัง (Back-to-Back mm)</label>
                            <input 
                              type="number" min="0" step="50" 
                              value={selectedArea.rackConfig?.backToBack ?? 150} 
                              onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, backToBack: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) })}
                              className="w-full p-2 border rounded-lg outline-none" 
                            />
                          </div>
                          <div>
                            <label className="block text-gray-600 mb-1 font-semibold text-[11px]">ทิศทางแถว (Orientation)</label>
                            <select 
                              value={selectedArea.rackConfig?.orientation || 'auto'}
                              onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, orientation: e.target.value })}
                              className="w-full p-2 border rounded-lg outline-none bg-white font-medium"
                            >
                              <option value="auto">Auto (คำนวณตามผัง)</option>
                              <option value="horizontal">แนวนอน (Horizontal)</option>
                              <option value="vertical">แนวตั้ง (Vertical)</option>
                            </select>
                          </div>
                        </div>

                        {/* 3. Safety Clearance & Air Gap (ตามที่ผู้ใช้ถามหา) */}
                        <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-3 space-y-2.5">
                          <span className="font-bold text-amber-900 text-xs flex items-center gap-1.5">
                            <Box size={13} className="text-amber-600" />
                            ระยะความปลอดภัย & สเปกคาน (Clearances & Air Gap)
                          </span>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                Air Gap เหนือพาเลท (mm)
                              </label>
                              <input 
                                type="number" min="50" max="600" step="50"
                                value={selectedArea.rackConfig?.airGap ?? 300} 
                                onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, airGap: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) })}
                                className="w-full p-2 bg-white border border-amber-300 rounded-lg text-xs font-bold text-gray-800 outline-none focus:ring-1 focus:ring-amber-500" 
                                title="ระยะเผื่อยกเหนือพาเลท (Clearance above pallet)"
                              />
                              <span className="text-[9px] text-gray-500 block mt-0.5">ระยะยกพ้นพาเลท (ปกติ 200-300mm)</span>
                            </div>
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                ความหนาคาน Beam (mm)
                              </label>
                              <input 
                                type="number" min="50" max="250" step="10"
                                value={selectedArea.rackConfig?.beamThickness ?? 100} 
                                onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, beamThickness: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) })}
                                className="w-full p-2 bg-white border border-amber-300 rounded-lg text-xs font-bold text-gray-800 outline-none focus:ring-1 focus:ring-amber-500" 
                              />
                              <span className="text-[9px] text-gray-500 block mt-0.5">ความหนาคานเหล็ก (ปกติ 100mm)</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-amber-200/50">
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                ระยะข้างพาเลท Margin (mm)
                              </label>
                              <input 
                                type="number" min="50" max="250" step="10"
                                value={selectedArea.rackConfig?.margin ?? 100} 
                                onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, margin: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) })}
                                className="w-full p-2 bg-white border border-amber-300 rounded-lg text-xs font-bold text-gray-800 outline-none" 
                              />
                              <span className="text-[9px] text-gray-500 block mt-0.5">ระยะห่างข้างพาเลทถึงเสา</span>
                            </div>
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                                พาเลทต่อช่อง (Pallets/Bay)
                              </label>
                              <select 
                                value={selectedArea.rackConfig?.palletsPerBay || 2}
                                onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, palletsPerBay: parseInt(e.target.value) || 2 })}
                                className="w-full p-2 bg-white border border-amber-300 rounded-lg text-xs font-bold text-gray-800 outline-none" 
                              >
                                <option value="1">1 พาเลท / Bay</option>
                                <option value="2">2 พาเลท / Bay (มาตรฐาน)</option>
                                <option value="3">3 พาเลท / Bay</option>
                              </select>
                              <span className="text-[9px] text-gray-500 block mt-0.5">จำนวนพาเลทใน 1 ช่อง Beam</span>
                            </div>
                          </div>
                        </div>

                        {/* 4. Advanced Engineering Options Accordion */}
                        <div className="border rounded-xl p-2.5 bg-gray-50/70 space-y-2">
                          <button 
                            type="button"
                            onClick={() => setShowAdvancedRack(!showAdvancedRack)}
                            className="w-full flex items-center justify-between text-[11px] font-bold text-gray-700 hover:text-gray-900"
                          >
                            <span className="flex items-center gap-1.5">
                              <Sliders size={12} className="text-gray-500" />
                              ตัวเลือกเสริมโครงสร้าง (Advanced Options)
                            </span>
                            {showAdvancedRack ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>

                          {showAdvancedRack && (
                            <div className="space-y-2 pt-2 border-t border-gray-200 text-xs">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="checkbox"
                                  checked={!!selectedArea.rackConfig?.singleRowStart}
                                  onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, singleRowStart: e.target.checked })}
                                  className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                                />
                                <div>
                                  <span className="font-semibold text-gray-800 block text-[11px]">เริ่มแถวเดี่ยวริมผนัง (Single Row Start)</span>
                                  <span className="text-[10px] text-gray-400">แถวแรกเป็นแถวเดี่ยวชิดผนัง แล้วจึงตามด้วยทางเดินรถและแถวคู่</span>
                                </div>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer pt-1">
                                <input 
                                  type="checkbox"
                                  checked={selectedArea.rackConfig?.hasBottomBeam !== false}
                                  onChange={e => updateAreaProp('rackConfig', { ...selectedArea.rackConfig, hasBottomBeam: e.target.checked })}
                                  className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                                />
                                <div>
                                  <span className="font-semibold text-gray-800 block text-[11px]">มีคานรับชั้นล่างสุด (Bottom Beam)</span>
                                  <span className="text-[10px] text-gray-400">หากปิด จะวางพาเลทชั้นที่ 1 บนพื้นคอนกรีตโดยตรง</span>
                                </div>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* 5. Pallet Capacity & Rack Height Badge */}
                        {(() => {
                          const c = getPalletCapacity(selectedArea);
                          const palletH = mapData.palletSize?.height || 1400;
                          const airGapVal = selectedArea.rackConfig?.airGap ?? 300;
                          const beamThickVal = selectedArea.rackConfig?.beamThickness ?? 100;
                          const levelsVal = selectedArea.rackConfig?.levels || 4;
                          const hasBottomBeamVal = selectedArea.rackConfig?.hasBottomBeam !== false;
                          const totalHeightMm = (hasBottomBeamVal ? beamThickVal : 0) + (levelsVal * (palletH + airGapVal + beamThickVal));
                          const totalRackHeightM = (totalHeightMm / 1000).toFixed(2);

                          return (
                            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center space-y-1 mt-2">
                              <div className="text-[10px] text-red-600 font-bold uppercase tracking-wider">ความจุแร็คนี้</div>
                              <div className="text-2xl font-black text-gray-900">{c.total.toLocaleString()} Pallets</div>
                              <div className="text-[11px] text-gray-600 font-medium">
                                {c.cols} bays × {c.rows} rows × {levelsVal} levels ({selectedArea.rackConfig?.palletsPerBay || 2} พาเลท/bay)
                              </div>
                              <div className="text-[10px] text-gray-500 pt-1 border-t border-red-200/50 flex items-center justify-center gap-3">
                                <span>ความสูงรวม: <strong>~{totalRackHeightM} ม.</strong></span>
                                <span>•</span>
                                <span>Air Gap: <strong>{airGapVal} mm</strong></span>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Advance to Step 4 / Back to Step 2 */}
              <div className="pt-3 border-t border-slate-200/80 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    if (onNavigateTab) {
                      onNavigateTab('config');
                    } else {
                      switchToSection('corridors');
                    }
                  }}
                  className="w-full py-2.5 px-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <span>ต่อไป: เลือกรุ่นรถยก Toyota & ฟลีท (ขั้นตอนที่ 2)</span>
                  <ArrowRight size={14} />
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => switchToSection('pallets')}
                    className="flex-1 py-1.5 px-2 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                  >
                    <ArrowLeft size={12} />
                    <span>ย้อนกลับ: สเปกพาเลท</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => switchToSection('corridors')}
                    className="flex-1 py-1.5 px-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                  >
                    <span>วางทางเดินรถ Aisle</span>
                    <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── 4. Corridors & Lanes (Porsche Style) ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.corridors ? 'border-amber-300 shadow-sm ring-1 ring-amber-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('corridors'); setActiveStep(5); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.corridors ? 'bg-gradient-to-r from-amber-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 border border-amber-100 flex items-center justify-center shrink-0">
                <Move size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">4. ทางเดินรถ AGV & เลนจราจร</span>
                  <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                    {(mapData.corridors || []).length} เลน
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {(mapData.corridors || []).length > 0 ? `${(mapData.corridors || []).length} ทางเดินรถในผัง` : 'คลิกเพื่อวาดเลนวิ่ง'}
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.corridors ? 'bg-amber-100 text-amber-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.corridors ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.corridors && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3 animate-in fade-in duration-150">
              <p className="text-[11px] text-gray-500">
                เลือกรูปแบบเลน แล้วลากวาดสี่เหลี่ยมบนผังเพื่อกำหนดเส้นทางเดินรถ:
              </p>

              {/* Corridor Cards */}
              <div className="grid grid-cols-1 gap-2.5">
                {CORRIDOR_OPTIONS.map(opt => {
                  const isSelected = activeLaneType === opt.id && (mode === 'draw_corridor' || selectedCorridor);
                  return (
                    <div 
                      key={opt.id}
                      onClick={() => handleSelectCorridorType(opt)}
                      className={`relative p-3 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                        isSelected 
                          ? 'border-2 border-red-600 bg-white shadow-xs' 
                          : 'border-gray-200 hover:border-gray-400 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: opt.color }} />
                            <span className="font-bold text-gray-900 text-xs">{opt.name}</span>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">{opt.desc}</p>
                        </div>

                        {/* Porsche Radio Indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          isSelected ? 'border-red-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Toolbar */}
              <div className="flex items-center gap-1.5 pt-1">
                <button
                  onClick={() => { setMode('draw_corridor'); setSelectedCorridor(null); setActiveStep(5); }}
                  className={`flex-1 py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    mode === 'draw_corridor' ? 'bg-red-600 text-white shadow-xs' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Move size={14} /> วาดทางเดินรถ
                </button>
                <button
                  onClick={() => { setMode('select'); setSelectedCorridor(null); }}
                  className={`py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    mode === 'select' && !selectedArea ? 'bg-gray-900 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <MousePointer2 size={14} /> เลือก
                </button>
                {pasteSelected && (
                  <button
                    type="button"
                    onClick={pasteSelected}
                    className="py-2 px-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1 transition-colors cursor-pointer bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100"
                    title="วางสิ่งที่คัดลอก (Ctrl+V)"
                  >
                    <ClipboardPaste size={13} /> วาง
                  </button>
                )}
              </div>

              {/* Selected Corridor Properties Drawer */}
              {selectedCorridor && (
                <div className="bg-white p-3.5 rounded-xl border-2 border-amber-200 shadow-sm space-y-3 mt-3 animate-in fade-in duration-100">
                  <div className="flex items-center justify-between border-b pb-2">
                    <span className="font-bold text-gray-900 text-xs">
                      ปรับแต่งทางเดินรถ: {selectedCorridor.label || 'Corridor'}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {copySelected && (
                        <button
                          type="button"
                          onClick={copySelected}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                          title="คัดลอกทางเดินรถนี้ (Ctrl+C)"
                        >
                          <Copy size={12} /> คัดลอก
                        </button>
                      )}
                      {duplicateSelected && (
                        <button
                          type="button"
                          onClick={duplicateSelected}
                          className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                          title="ทำซ้ำทางเดินรถนี้ (Ctrl+D)"
                        >
                          <Copy size={12} /> ทำซ้ำ
                        </button>
                      )}
                      <button 
                        onClick={deleteCorridor} 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                        title="ลบทางเดินรถนี้"
                      >
                        <Trash2 size={12} /> ลบ
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2.5 text-xs">
                    <div>
                      <label className="block text-gray-600 mb-1">ชื่อเรียก (Label)</label>
                      <input 
                        type="text" 
                        value={selectedCorridor.label || ''} 
                        onChange={e => updateCorridorProp('label', e.target.value)}
                        placeholder="เช่น Main Highway 1"
                        className="w-full p-2 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" 
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-gray-600 mb-1">จำนวนเลนวิ่ง</label>
                        <select 
                          value={selectedCorridor.lanes || 2}
                          onChange={e => updateCorridorProp('lanes', parseInt(e.target.value) || 2)}
                          className="w-full p-2 border rounded-lg outline-none bg-white"
                        >
                          <option value="1">1 เลน</option>
                          <option value="2">2 เลน</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-600 mb-1">ทิศทางจราจร</label>
                        <select 
                          value={selectedCorridor.direction || 'bidirectional'}
                          onChange={e => updateCorridorProp('direction', e.target.value)}
                          className="w-full p-2 border rounded-lg outline-none bg-white"
                        >
                          <option value="bidirectional">สองทิศทาง (2-Way)</option>
                          <option value="split">แยกซ้าย/ขวา (Split)</option>
                          <option value="forward">วันเวย์ไปข้างหน้า</option>
                          <option value="backward">วันเวย์ย้อนกลับ</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Advance to Step 5 / Back to Step 3 */}
              <div className="pt-3 border-t border-slate-200/80 space-y-2">
                <button
                  type="button"
                  onClick={() => switchToSection('stations')}
                  className="w-full py-2.5 px-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <span>ต่อไป: จุดรับ-ส่ง & เส้นทางไหล (ขั้นตอนที่ 5)</span>
                  <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => switchToSection('racking')}
                  className="w-full py-1.5 px-3 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                >
                  <ArrowLeft size={12} />
                  <span>ย้อนกลับไประบบแร็ค (ขั้นตอนที่ 3)</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── 5. Stations & Flows ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.stations ? 'border-purple-300 shadow-sm ring-1 ring-purple-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('stations'); setActiveStep(6); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.stations ? 'bg-gradient-to-r from-purple-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center shrink-0">
                <ArrowRightLeft size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">5. จุดรับ-ส่ง & เส้นทางไหล</span>
                  <span className="text-[10px] font-semibold bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200">
                    {(mapData.areas || []).filter(a => a.type === 'pickup' || a.type === 'dropoff').length} สถานี
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {(mapData.flows || []).length} เส้นทางไหลของสินค้า
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.stations ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.stations ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.stations && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3 animate-in fade-in duration-150">
              <div className="grid grid-cols-1 gap-2">
                {STATION_OPTIONS.map(opt => {
                  const isSelected = activeRackType === opt.type && (mode === 'draw_area' || selectedArea?.type === opt.type);
                  return (
                    <div 
                      key={opt.type}
                      onClick={() => handleSelectStationType(opt)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        isSelected 
                          ? 'border-2 border-red-600 bg-white shadow-xs' 
                          : 'border-gray-200 hover:border-gray-400 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                        <div>
                          <div className="font-bold text-gray-900 text-xs">{opt.name}</div>
                          <div className="text-[11px] text-gray-400">{opt.desc}</div>
                        </div>
                      </div>

                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? 'border-red-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected Station (Pickup / Drop-off) Pallet Configuration */}
              {selectedArea && (selectedArea.type === 'pickup' || selectedArea.type === 'dropoff') && (
                <div id="selected-station-drawer" className="bg-white p-3 rounded-xl border-2 border-purple-200 shadow-xs space-y-2.5 animate-in fade-in duration-100">
                  <div className="flex items-center justify-between border-b pb-2">
                    <span className="font-bold text-gray-900 text-xs flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: selectedArea.type === 'pickup' ? '#10b981' : '#ef4444' }} />
                      จุด {selectedArea.type === 'pickup' ? 'Pickup (รับสินค้า)' : 'Drop-off (ส่งสินค้า)'}: {selectedArea.label || selectedArea.id?.slice(0, 8)}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {copySelected && (
                        <button
                          type="button"
                          onClick={copySelected}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                          title="คัดลอกสถานีนี้ (Ctrl+C)"
                        >
                          <Copy size={12} /> คัดลอก
                        </button>
                      )}
                      <button 
                        type="button"
                        onClick={deleteArea} 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 text-[11px] font-medium transition-colors"
                        title="ลบจุดนี้"
                      >
                        <Trash2 size={12} /> ลบ
                      </button>
                    </div>
                  </div>

                  {/* Toggle Simulate Pallet */}
                  <div className="flex items-center justify-between pt-0.5">
                    <div>
                      <span className="text-xs font-semibold text-gray-800 block">จำลองพาเลทบนจุดนี้</span>
                      <span className="text-[10px] text-gray-400">แสดงพาเลท 2D/3D บนแท่นรับ-ส่ง</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={!!selectedArea.stationConfig?.showPallet}
                        onChange={e => updateAreaProp('stationConfig', {
                          ...selectedArea.stationConfig,
                          showPallet: e.target.checked
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {selectedArea.stationConfig?.showPallet && (
                    <div className="space-y-2 pt-1 border-t border-purple-100">
                      <label className="block text-[11px] font-semibold text-gray-700">สถานะพาเลท</label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { id: 'full', label: '📦 พาเลทเต็มสินค้า' },
                          { id: 'empty', label: '🪵 พาเลทเปล่า' }
                        ].map(st => (
                          <button
                            key={st.id}
                            type="button"
                            onClick={() => updateAreaProp('stationConfig', {
                              ...selectedArea.stationConfig,
                              palletStatus: st.id
                            })}
                            className={`py-1.5 px-2 rounded-lg text-xs font-bold border transition-all ${
                              (selectedArea.stationConfig?.palletStatus || 'full') === st.id
                                ? 'bg-purple-600 text-white border-purple-600 shadow-2xs'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {st.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Material Flows List */}
              <div className="pt-2 border-t space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800 text-xs">เส้นทางไหลสินค้า (Material Flows)</span>
                  <button 
                    onClick={() => setMapData(p => ({ ...p, flows: [...(p.flows || []), { id: `flow_${Date.now()}`, sourceAreaId: null, targetAreaId: null }] }))}
                    className="text-[11px] text-red-600 font-bold hover:underline flex items-center gap-1"
                  >
                    <PlusCircle size={13} /> เพิ่มเส้นทาง
                  </button>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(mapData.flows || []).map((flow, idx) => (
                    <div key={flow.id} className="p-2.5 bg-white border border-gray-200 rounded-lg text-[11px] flex items-center justify-between">
                      <span>Flow #{idx + 1}</span>
                      <button 
                        onClick={() => setMapData(p => ({ ...p, flows: p.flows.filter(f => f.id !== flow.id) }))}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Step Button */}
              <div className="pt-3 border-t border-slate-200/80 space-y-2">
                <button
                  type="button"
                  onClick={() => switchToSection('shifts')}
                  className="w-full py-2.5 px-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <span>ต่อไป: กำหนดกะทำงาน & พักเบรค (ขั้นตอนที่ 6)</span>
                  <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => switchToSection('corridors')}
                  className="w-full py-1.5 px-3 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                >
                  <ArrowLeft size={12} />
                  <span>ย้อนกลับไปทางเดินรถ Aisle</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── 6. Working Shifts & Break Times (Fleet & Battery Calculation) ─── */}
        <div className={`rounded-2xl border transition-all duration-200 bg-white overflow-hidden ${
          openSections.shifts ? 'border-teal-300 shadow-sm ring-1 ring-teal-100' : 'border-slate-200/80 shadow-xs hover:border-slate-300'
        }`}>
          <div 
            onClick={() => { toggleSection('shifts'); setActiveStep(7); }}
            className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
              openSections.shifts ? 'bg-gradient-to-r from-teal-50/40 to-white' : 'bg-white hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-teal-50 text-teal-600 border border-teal-100 flex items-center justify-center shrink-0">
                <Clock size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-xs">6. กะการทำงาน & ช่วงเวลาพักเบรค</span>
                  <span className="text-[10px] font-semibold bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded border border-teal-200">
                    {shiftConfig.shiftsPerDay} กะ/วัน
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  ทำงานจริง {netDailyHours} ชม./วัน • พัก {totalBreakMinutes} นาที/กะ
                </div>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
              openSections.shifts ? 'bg-teal-100 text-teal-700' : 'text-gray-400 hover:bg-slate-100'
            }`}>
              {openSections.shifts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>

          {openSections.shifts && (
            <div className="p-3.5 bg-slate-50/50 border-t border-slate-100 space-y-3.5 animate-in fade-in duration-150 text-xs">
              {/* 1. Shift Selection Presets */}
              <div className="space-y-1.5">
                <label className="font-bold text-gray-800 text-xs flex items-center gap-1.5">
                  <Calendar size={13} className="text-teal-600" />
                  จำนวนกะการทำงาน (Operating Shifts)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { shifts: 1, hours: 8, label: '1 กะ', desc: '8 ชม./วัน (กะปกติ)' },
                    { shifts: 2, hours: 16, label: '2 กะ', desc: '16 ชม./วัน (มาตรฐาน)' },
                    { shifts: 3, hours: 24, label: '3 กะ', desc: '24 ชม./วัน (24/7)' }
                  ].map(s => {
                    const isSelected = shiftConfig.shiftsPerDay === s.shifts;
                    return (
                      <button
                        key={s.shifts}
                        type="button"
                        onClick={() => updateShiftConfig('shiftsPerDay', s.shifts)}
                        className={`p-2 rounded-xl border text-center transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-teal-600 text-white border-teal-600 shadow-xs ring-2 ring-teal-200'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-black text-xs">{s.label}</div>
                        <div className={`text-[10px] mt-0.5 ${isSelected ? 'text-teal-100' : 'text-gray-400'}`}>
                          {s.hours} ชม./วัน
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Break Schedules */}
              <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-3 shadow-xs">
                <div className="font-bold text-gray-900 text-xs flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} className="text-teal-600" />
                    ช่วงเวลาพักเบรคต่อกะ (Break Schedules)
                  </span>
                  <span className="text-[11px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                    รวมพัก {totalBreakMinutes} นาที
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] text-gray-500 font-medium block mb-1">
                      พักมื้อหลัก (Lunch / Meal)
                    </label>
                    <select
                      value={shiftConfig.lunchBreakMinutes || 60}
                      onChange={(e) => updateShiftConfig('lunchBreakMinutes', Number(e.target.value))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-800 font-semibold focus:outline-none focus:border-teal-500"
                    >
                      <option value={30}>30 นาที</option>
                      <option value={45}>45 นาที</option>
                      <option value={60}>60 นาที (1 ชม.)</option>
                      <option value={90}>90 นาที</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] text-gray-500 font-medium block mb-1">
                      พักย่อยระหว่างงาน (Short Break)
                    </label>
                    <select
                      value={shiftConfig.shortBreakMinutes || 15}
                      onChange={(e) => updateShiftConfig('shortBreakMinutes', Number(e.target.value))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-800 font-semibold focus:outline-none focus:border-teal-500"
                    >
                      <option value={10}>10 นาที (x2 ครั้ง = 20น.)</option>
                      <option value={15}>15 นาที (x2 ครั้ง = 30น.)</option>
                      <option value={20}>20 นาที (x2 ครั้ง = 40น.)</option>
                      <option value={0}>ไม่มีพักย่อย</option>
                    </select>
                  </div>
                </div>

                {/* Daily Pallet Demand Input */}
                <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-800 text-[11px]">ยอดพาเลทเป้าหมายต่อวัน (Daily Pallet Demand)</div>
                    <div className="text-[10px] text-gray-400">ใช้คำนวณจำนวนรถยกที่เหมาะสม</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <input
                      type="number"
                      min="50"
                      step="50"
                      value={shiftConfig.dailyPalletDemand || 350}
                      onChange={(e) => updateShiftConfig('dailyPalletDemand', Number(e.target.value))}
                      className="w-20 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs text-right font-bold text-gray-800 focus:outline-none focus:border-teal-500"
                    />
                    <span className="text-[10px] text-gray-500 font-medium">พาเลท</span>
                  </div>
                </div>
              </div>

              {/* 3. Real-time Calculation Results Card */}
              <div className="bg-gradient-to-br from-teal-900 to-slate-900 text-white rounded-xl p-3.5 space-y-3 shadow-md border border-teal-700/50">
                <div className="flex items-center justify-between border-b border-teal-700/50 pb-2">
                  <div className="flex items-center gap-1.5 font-bold text-xs text-teal-300">
                    <Zap size={14} className="text-amber-400" />
                    ผลการวิเคราะห์เวลา & จำนวนรถที่ต้องใช้
                  </div>
                  <span className="text-[10px] bg-teal-800/80 px-2 py-0.5 rounded-full text-teal-200 font-semibold border border-teal-600/40">
                    Toyota Engine Sizing
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-950/60 p-2 rounded-lg border border-teal-800/40">
                    <div className="text-[10px] text-teal-200/80">เวลาทำงานจริงสุทธิ</div>
                    <div className="text-base font-black text-white mt-0.5">{netHoursPerShift} ชม./กะ</div>
                    <div className="text-[9px] text-teal-300/70">รวม {netDailyHours} ชม./วัน</div>
                  </div>

                  <div className="bg-slate-950/60 p-2 rounded-lg border border-teal-800/40">
                    <div className="text-[10px] text-teal-200/80">จำนวนรถยกที่ต้องใช้</div>
                    <div className="text-base font-black text-amber-300 mt-0.5">{calculatedFleet} คัน</div>
                    <div className="text-[9px] text-amber-200/70">Throughput {requiredThroughputHr} P/ชม.</div>
                  </div>
                </div>

                {/* Battery Strategy Recommendation */}
                <div className="bg-teal-950/70 rounded-lg p-2.5 border border-teal-600/40 text-[11px] space-y-1">
                  <div className="flex items-center gap-1.5 font-bold text-teal-300">
                    <BatteryCharging size={14} className="text-emerald-400 shrink-0" />
                    คำแนะนำระบบแบตเตอรี่ (Battery Strategy):
                  </div>
                  {shiftConfig.shiftsPerDay >= 2 ? (
                    <p className="text-slate-200 text-[10px] leading-relaxed">
                      💡 แนะนำใช้ <strong className="text-white font-bold">Lithium-ion (Li-ion)</strong> พร้อมระบบ <strong className="text-amber-300">Opportunity Fast Charge</strong> ช่วงพัก {totalBreakMinutes} นาที สามารถคืนประจุได้ประมาณ <strong className="text-emerald-300 font-bold">+{estimatedRechargePct}%</strong> ทำให้วิ่งต่อเนื่องได้ทั้ง {shiftConfig.shiftsPerDay} กะ โดยไม่ต้องสลับก้อนแบตเตอรี่ (Zero Battery Swapping)
                    </p>
                  ) : (
                    <p className="text-slate-200 text-[10px] leading-relaxed">
                      💡 1 กะการทำงาน สามารถเลือกใช้ได้ทั้ง <strong className="text-white font-bold">Lead-Acid</strong> (ชาร์จข้ามคืน 8 ชม.) หรือ <strong className="text-teal-300 font-bold">Lithium-ion</strong> โดยไม่ต้องมีห้องชาร์จแบตเตอรี่แยก
                    </p>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="pt-2 border-t border-slate-200/80 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    if (onNavigateTab) onNavigateTab('config');
                  }}
                  className="w-full py-2.5 px-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                >
                  <span>เสร็จสิ้นขั้นตอนที่ 1 → ไปเลือกรุ่นรถยก Toyota (ขั้นตอนที่ 2)</span>
                  <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => switchToSection('stations')}
                  className="w-full py-1.5 px-3 bg-white hover:bg-slate-100 text-slate-600 font-medium rounded-lg text-[11px] flex items-center justify-center gap-1 border border-slate-200 transition-all cursor-pointer"
                >
                  <ArrowLeft size={12} />
                  <span>ย้อนกลับไปจุดรับ-ส่ง & เส้นทางไหล</span>
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
